import React, {
	useState,
	useEffect,
	useCallback,
	useContext,
	useRef,
	useLayoutEffect,
	useMemo,
} from "react";
import { createPortal } from "react-dom";

import { toast } from "react-toastify";
import useSound from "use-sound";
import {
	Users, Plus, Send, MessageSquarePlus, Loader2, Paperclip, X, FileImage, FileVideo,
	FileAudio, FileText, FileIcon, FileArchive, FileSpreadsheet, Reply, Smile, MoreVertical, Trash2, AlertTriangle, MessageCircle, Pencil, Check, Mic, Eye, Download, Sparkles, SpellCheck, MessageSquare, Sticker, Search, ChevronUp, ChevronDown,
} from "lucide-react";

import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { MultiSelect } from "../../components/ui/multi-select";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

import MainContainer from "../../components/MainContainer";
import somDeAviso from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useThemeContext } from "../../context/DarkMode";
import { getPublicFileUrl } from "../../config";
import RecordingTimer from "../../components/MessageInput/RecordingTimer";
import { paraWav } from "../../components/MessageInput/wav";
import VisualizadorDeArquivo from "../../components/VisualizadorDeArquivo";
import MiniaturaDePdf from "../../components/MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import FigurinhaPicker from "../../components/FigurinhaPicker";
import AssistenteDeTexto from "../../components/MessageInput/AssistenteDeTexto";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";

const EXTENSOES_PERMITIDAS = [
	".jpg", ".jpeg", ".jfif", ".png", ".gif", ".webp", ".bmp", ".tiff", ".heic", ".heif",
	".mp3", ".ogg", ".oga", ".opus", ".wav", ".m4a", ".aac", ".amr", ".flac",
	".mp4", ".3gp", ".mov", ".avi", ".mkv", ".webm",
	".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".odt", ".ods", ".odp",
	".rtf", ".txt", ".csv", ".md", ".json", ".xml", ".epub",
	".zip", ".rar", ".7z", ".gz", ".tgz",
];

const ACCEPT_STRING = EXTENSOES_PERMITIDAS.join(",");

const EMOJIS_PADROES = [
	{ emoji: "👍", label: "Legal" },
	{ emoji: "❤️", label: "Coração" },
	{ emoji: "😂", label: "Rindo" },
	{ emoji: "😮", label: "Uau" },
	{ emoji: "😢", label: "Triste" },
	{ emoji: "🙏", label: "Obrigado" },
	{ emoji: "🔥", label: "Fogo" },
	{ emoji: "🎉", label: "Festa" },
];

const FORMATOS_AUDIO = [
	{ mime: "audio/ogg;codecs=opus", ext: "ogg" },
	{ mime: "audio/webm;codecs=opus", ext: "webm" },
	{ mime: "audio/mp4", ext: "m4a" },
	{ mime: "audio/webm", ext: "webm" }
];

const escolherFormatoAudio = () => {
	if (typeof MediaRecorder === "undefined") return null;
	return FORMATOS_AUDIO.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
		mime: "",
		ext: "webm",
	};
};

const WHATSAPP_LIGHT = {
	bgChat: "#efeae2",
	bgChatDots:
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'><g fill='%23000000' fill-opacity='0.028'><circle cx='120' cy='120' r='1.5'/><circle cx='280' cy='80' r='1.5'/><circle cx='360' cy='200' r='1.5'/><circle cx='60' cy='300' r='1.5'/><circle cx='200' cy='360' r='1.5'/><circle cx='320' cy='320' r='1.5'/></g></svg>\")",
	balaoEnviada: "#d9fdd3",
	balaoRecebida: "#ffffff",
	corTextoEnviada: "#111b21",
	corTextoRecebida: "#111b21",
	corTimeEnviada: "#667781",
	corTimeRecebida: "#667781",
	verdeWpp: "#00a884",
	verdeWppEscuro: "#008069",
	nomeAutorGrupo: "#111b21",
	bgSidebar: "#ffffff",
	bgHeader: "#f0f2f5",
	bgInput: "#f0f2f5",
	bgQuotedEnviada: "#c9f4c6",
	bgQuotedRecebida: "#f0f2f5",
	borderQuoted: "#00a884",
	borderQuotedMinha: "#008069",
	bgChipReaction: "rgba(255,255,255,0.8)",
	bgChipReactionMinha: "rgba(255,255,255,0.9)",
	bgChipReactionEu: "#e8f4ee",
	bgChipReactionEuMinha: "#e9f3f0",
	bgSidebarItemSelected: "#f0f2f5",
	bgSidebarHover: "#f5f6f6",
	bgSidebarSeparator: "#f0f2f5",
	textoMuted: "#667781",
	badgeUnread: "#00a884",
	borderSeparator: "#e9edef",
	bgEmptyChat: "#f0f2f5",
	popoverBg: "#ffffff",
	popoverBorder: "#e9edef",
	avatarBgGrupo: "#d1f4cc",
	avatarBgPessoa: "#e3f0da",
	avatarIconColor: "#008069",
	barraResponderBg: "#ffffff",
	chipAnexoBg: "#ffffff",
	inputBg: "#ffffff",
};

const WHATSAPP_DARK = {
	bgChat: "#0b141a",
	bgChatDots:
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'><g fill='%23ffffff' fill-opacity='0.04'><circle cx='120' cy='120' r='1.5'/><circle cx='280' cy='80' r='1.5'/><circle cx='360' cy='200' r='1.5'/><circle cx='60' cy='300' r='1.5'/><circle cx='200' cy='360' r='1.5'/><circle cx='320' cy='320' r='1.5'/></g></svg>\")",
	balaoEnviada: "#005c4b",
	balaoRecebida: "#202c33",
	corTextoEnviada: "#e9edef",
	corTextoRecebida: "#e9edef",
	corTimeEnviada: "#8696a0",
	corTimeRecebida: "#8696a0",
	verdeWpp: "#00a884",
	verdeWppEscuro: "#00cf8c",
	nomeAutorGrupo: "#e9edef",
	bgSidebar: "#111b21",
	bgHeader: "#202c33",
	bgInput: "#202c33",
	bgQuotedEnviada: "#0a4c3e",
	bgQuotedRecebida: "#2b373d",
	borderQuoted: "#00a884",
	borderQuotedMinha: "#53bdeb",
	bgChipReaction: "rgba(32,44,51,0.92)",
	bgChipReactionMinha: "rgba(0,92,75,0.92)",
	bgChipReactionEu: "#17423b",
	bgChipReactionEuMinha: "#0b624f",
	bgSidebarItemSelected: "#2a3942",
	bgSidebarHover: "#202c33",
	bgSidebarSeparator: "#233138",
	textoMuted: "#8696a0",
	badgeUnread: "#00a884",
	borderSeparator: "#233138",
	bgEmptyChat: "#222e35",
	popoverBg: "#233138",
	popoverBorder: "#1c2b33",
	avatarBgGrupo: "#175c49",
	avatarBgPessoa: "#2c3d37",
	avatarIconColor: "#00cf8c",
	barraResponderBg: "#202c33",
	chipAnexoBg: "#2a3942",
	inputBg: "#2a3942",
};

const AvatarUsuario = ({ user = null, name = "", profilePicUrl = null, size = "md", className = "" }) => {
	const { darkMode } = useThemeContext();
	const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;
	const url = user?.profilePicUrl || profilePicUrl;
	const nomeMostrar = user?.name || name || "?";
	const tamanhos = {
		sm: "h-6 w-6 text-[10px]",
		md: "h-10 w-10 text-xs",
		lg: "h-9 w-9 text-xs",
	};
	const dimensao = tamanhos[size] || tamanhos.md;
	const publicUrl = url ? getPublicFileUrl(url) : undefined;
	return (
		<Avatar className={`${dimensao} shrink-0 ${className}`}>
			{publicUrl ? (
				<AvatarImage src={publicUrl} alt={nomeMostrar} referrerPolicy="no-referrer" />
			) : null}
			<AvatarFallback
				className={`${dimensao.replace(/h-\d+ w-\d+ /, "")} font-semibold`}
				style={{ backgroundColor: tema.avatarBgPessoa, color: tema.avatarIconColor }}
			>
				{getInitials(nomeMostrar)}
			</AvatarFallback>
		</Avatar>
	);
};

const getInitials = (name) =>
	(name || "?")
		.split(" ")
		.slice(0, 2)
		.map((n) => n[0])
		.join("")
		.toUpperCase();

const formatTime = (dateStr) => {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	const today = new Date();
	const isToday =
		date.getDate() === today.getDate() &&
		date.getMonth() === today.getMonth() &&
		date.getFullYear() === today.getFullYear();
	if (isToday) {
		return date.toLocaleTimeString("pt-BR", {
			hour: "2-digit",
			minute: "2-digit",
		});
	}
	return date.toLocaleDateString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
	});
};

const humanFileSize = (bytes) => {
	if (!bytes && bytes !== 0) return "";
	const units = ["B", "KB", "MB", "GB"];
	let i = 0;
	let v = Number(bytes) || 0;
	while (v >= 1024 && i < units.length - 1) {
		v /= 1024;
		i++;
	}
	return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const tipoDeMidia = (nomeArquivo) => {
	const ext = extensaoDe(nomeArquivo);
	if (!ext) return "application";
	if ([".jpg", ".jpeg", ".jfif", ".png", ".gif", ".webp", ".bmp", ".tiff", ".heic", ".heif"].includes(ext)) return "image";
	if ([".mp4", ".3gp", ".mov", ".avi", ".mkv", ".webm"].includes(ext)) return "video";
	if ([".mp3", ".ogg", ".oga", ".opus", ".wav", ".m4a", ".aac", ".amr", ".flac"].includes(ext)) return "audio";
	return "application";
};

const iconeDoAnexo = (tipo, className = "h-5 w-5") => {
	switch (tipo) {
		case "image":
			return <FileImage className={className} />;
		case "video":
			return <FileVideo className={className} />;
		case "audio":
			return <FileAudio className={className} />;
		case "application":
		default:
			return <FileText className={className} />;
	}
};

const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

const IconeDoDocumento = ({ extensao }) => {
	if (PLANILHAS.includes(extensao))
		return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
	if (COMPACTADOS.includes(extensao))
		return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
	return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
};

const RenderizarMidia = ({ message, onAbrirImagem, abrirArquivo, transcrevendo, transcription, transcribedAt, onTranscrever }) => {
		const { darkMode } = useThemeContext();
		const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;
		if (!message || !message.mediaUrl) return null;
		const url = getPublicFileUrl(message.mediaUrl);
		const mediaType = message.mediaType || tipoDeMidia(message.mediaUrl);
		const nomeArquivo = decodeURIComponent(String(message.mediaUrl || "").split("/").pop() || "");
		const extensao = extensaoDe(message.mediaUrl).replace(".", "").toUpperCase() || "ARQUIVO";

		if (mediaType === "sticker") {
			// IGUAL ao MessagesList/index.jsx de tickets (L1729-L1769):
			//   • tamanho fixo 150x150px
			//   • object-contain (preserva aspect ratio da figurinha original)
			//   • SEM botão de zoom / onClick capturando (só desenha, não abre modal)
			//   • draggable nativo para poder arrastar/salvar com direito
			if (!url) {
				return (
					<div
						className="mb-1 flex h-[150px] w-[150px] items-center justify-center rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
						title="Figurinha não carregada"
					>
						Figurinha
					</div>
				);
			}
			return (
				<div className="mb-1" style={{ background: "transparent" }}>
					<img
						src={url}
						alt={nomeArquivo}
						loading="eager"
						decoding="async"
						draggable
						onContextMenu={(e) => {
							try { e.stopPropagation(); } catch {}
						}}
						style={{ WebkitUserDrag: "auto", userSelect: "auto" }}
						className="h-[150px] w-[150px] object-contain"
					/>
				</div>
			);
		}

		if (mediaType === "image") {
			return (
				<button
					type="button"
					onClick={(e) => { e.preventDefault(); onAbrirImagem?.(url, nomeArquivo); }}
					className="block overflow-hidden rounded-xl text-left"
					style={{ background: "transparent", border: 0, padding: 0, cursor: "zoom-in" }}
				>
					<img
						src={url}
						alt={nomeArquivo}
						className="max-h-80 w-full max-w-[320px] rounded-xl object-contain"
						loading="lazy"
					/>
				</button>
			);
		}

		if (mediaType === "video") {
			return (
				<video
					src={url}
					controls
					preload="metadata"
					className="max-h-96 w-full max-w-[320px] rounded-xl bg-black"
				/>
			);
		}

		if (mediaType === "audio") {
			const temTranscricao =
				typeof transcription === "string" && transcription.trim() !== "";
			const handleCopiarTranscricao = async () => {
				if (!transcription) return;
				try {
					await navigator.clipboard.writeText(transcription);
				} catch (_) {}
			};
			const handleRefazerTranscricao = () => {
				onTranscrever?.({ forcar: true });
			};
			return (
				<div>
					<div
						className="flex w-full max-w-[320px] items-center gap-2 rounded-xl p-2"
						style={{ backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}
					>
						<FileAudio className="h-6 w-6 shrink-0" style={{ color: tema.textoMuted }} />
						<audio
							src={url}
							controls
							preload="metadata"
							className="flex-1 min-w-0"
						/>
					</div>
					{temTranscricao ? (
						<div
							className="mt-2 w-full max-w-[320px] rounded-lg border overflow-hidden"
							style={{
								backgroundColor: darkMode ? "rgba(255,255,255,0.03)" : "#ffffff",
								borderColor: tema.borderSeparator,
							}}
						>
							<div
								className="flex items-center justify-between px-3 pt-2.5 pb-2"
								style={{
									backgroundColor: darkMode ? "rgba(255,255,255,0.02)" : "#fafafa",
									borderBottom: `1px solid ${tema.borderSeparator}`,
								}}
							>
								<div
									className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider"
									style={{ color: "#8696a0" }}
								>
									<CopyIcon className="h-3.5 w-3.5" style={{ strokeWidth: 2.2 }} />
									Transcrição
								</div>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleCopiarTranscricao();
										}}
										title="Copiar transcrição"
										className="flex h-7 w-7 items-center justify-center rounded-full transition-colors"
										style={{ color: "#8696a0" }}
										onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)")}
										onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
									>
										<CopyIcon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleRefazerTranscricao();
										}}
										title="Transcrever novamente"
										className="flex h-7 w-7 items-center justify-center rounded-full transition-colors"
										style={{ color: "#8696a0" }}
										onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)")}
										onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
									>
										<RefreshIcon className="h-4 w-4" />
									</button>
								</div>
							</div>
							<div
								className="px-3 py-3 text-[14px] leading-relaxed whitespace-pre-wrap"
								style={{
									color: darkMode ? "#e9edef" : "#111b21",
								}}
							>
								{transcription}
							</div>
						</div>
					) : transcrevendo ? (
						<div
							className="mt-2 inline-flex items-center gap-2 rounded-md px-1 py-1 text-[14px]"
							style={{ color: "#8696a0" }}
						>
							⏳ Transcrevendo...
						</div>
					) : transcribedAt ? null : (
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onTranscrever?.();
							}}
							className="mt-2 inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[14px] font-normal transition-colors"
							style={{
								backgroundColor: "transparent",
								border: "none",
								color: "#54656f",
								cursor: "pointer",
							}}
						>
							<CopyIcon className="h-4 w-4" style={{ strokeWidth: 2 }} />
							Transcrever
						</button>
					)}
				</div>
			);
		}

		const urlMedia = url;
		const extensaoLower = extensaoDe(message.mediaUrl);
		const rotulo = extensaoLower
			? `Arquivo ${extensaoLower.replace(".", "").toUpperCase()}`
			: "Arquivo";

		return (
			<div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
				{extensaoLower === ".pdf" && (
					<div className="mb-2">
						<MiniaturaDePdf
							url={urlMedia}
							onAbrir={() => abrirArquivo?.(message.id)}
						/>
					</div>
				)}

				<div className="flex items-center gap-2">
					<IconeDoDocumento extensao={extensaoLower} />
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-medium">{rotulo}</p>
						<p className="text-xs text-muted-foreground">
							{extensaoLower ? extensaoLower.replace(".", "").toUpperCase() : "documento"}
						</p>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 shrink-0"
						title="Visualizar"
						onClick={() => abrirArquivo?.(message.id)}
					>
						<Eye className="h-4 w-4" />
					</Button>
					<Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0">
						<a
							href={urlMedia}
							target="_blank"
							rel="noopener noreferrer"
							title="Baixar"
						>
							<Download className="h-4 w-4" />
						</a>
					</Button>
				</div>
			</div>
		);
	};

const DownloadArrowIcon = ({ className = "h-5 w-5" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="7 10 12 15 17 10" />
		<line x1="12" x2="12" y1="15" y2="3" />
	</svg>
);

const EyeIcon = ({ className = "h-5 w-5" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
		<circle cx="12" cy="12" r="3" />
	</svg>
);

const CopyIcon = ({ className = "h-4 w-4" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
		<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
	</svg>
);

const RefreshIcon = ({ className = "h-4 w-4" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
		<path d="M21 3v5h-5" />
	</svg>
);

const DownloadIcon = ({ className = "h-6 w-6" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="7 10 12 15 17 10" />
		<line x1="12" x2="12" y1="15" y2="3" />
	</svg>
);

const previewMensagemCitada = (msg) => {
	if (!msg) return "";
	if (msg.mediaUrl && !msg.body) {
		const t = msg.mediaType || tipoDeMidia(msg.mediaUrl);
		switch (t) {
			case "image": return "📷 Imagem";
			case "video": return "🎥 Vídeo";
			case "audio": return "🎵 Áudio";
			default: return "📎 Arquivo";
		}
	}
	const s = (msg.body || "").replace(/\s+/g, " ").trim();
	return s.length > 80 ? `${s.slice(0, 80)}…` : s;
};

const escapeRegex = (s) =>
	String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ================================================================
// Destaca trechos de texto correspondentes à busca (igual WhatsApp Web).
//
// Usada no corpo das mensagens e nas quotedMsg quando a busca (lena)
// está aberta no header da conversa (igual InternalChatWidget).
// - `destacar=true` (match atual) → fundo #ffcc4d (mais forte).
// - `destacar=false` (outros matches) → fundo #fff3a3 (mais claro).
// ================================================================
const destacarTexto = (texto, busca, destacar) => {
	const t = String(texto == null ? "" : texto);
	const b = String(busca || "").trim();
	if (!b || !t) return t;
	const needles = [];
	const lowerT = t.toLowerCase();
	const lowerB = b.toLowerCase();
	let cursor = 0;
	while (cursor < t.length) {
		const found = lowerT.indexOf(lowerB, cursor);
		if (found < 0) break;
		needles.push({ start: found, end: found + b.length });
		cursor = found + b.length;
	}
	if (needles.length === 0) return t;
	const nodes = [];
	let anterior = 0;
	needles.forEach((r, i) => {
		if (r.start > anterior) nodes.push(t.slice(anterior, r.start));
		nodes.push(
			<mark
				key={i}
				style={{
					backgroundColor: destacar ? "#ffcc4d" : "#fff3a3",
					color: "#111b21",
					borderRadius: 2,
					padding: "0 1px",
				}}
			>
				{t.slice(r.start, r.end)}
			</mark>
		);
		anterior = r.end;
	});
	if (anterior < t.length) nodes.push(t.slice(anterior));
	return nodes;
};

const nomeUsuarioNaMsg = (msg) => {
	if (!msg) return "Usuário";
	if (msg.fromUser?.name) return msg.fromUser.name;
	return "Usuário";
};

// #region debug-point popup-duplicate
const DBG_KEY = "dbg_internal_chat_popup_v1";
const dbgPush = (ev) => {
	try {
		const raw = localStorage.getItem(DBG_KEY) || "[]";
		const arr = JSON.parse(raw);
		arr.push({ t: Date.now(), ...ev });
		while (arr.length > 1500) arr.shift();
		localStorage.setItem(DBG_KEY, JSON.stringify(arr));
	} catch (_) {}
};
if (typeof window !== "undefined") {
	try {
		const raw = localStorage.getItem(DBG_KEY);
		if (!raw) {
			localStorage.setItem(DBG_KEY, JSON.stringify([]));
		}
		window.onerror = (msg, file, line, col, err) => {
			dbgPush({
				ev: "uncaught_error",
				msg: String(msg || "").slice(0, 180),
				line: line,
				col: col,
				stack: err && err.stack ? String(err.stack).slice(0, 400) : "",
			});
		};
		window.onunhandledrejection = (e) => {
			dbgPush({
				ev: "unhandled_promise",
				reason: e && e.reason ? String(e.reason).slice(0, 400) : "",
			});
		};
	} catch (_) {}
}
// #endregion

const EmojiPopoverInline = ({ onPick, onReply, onEdit, onClose, canEdit }) => {
	dbgPush({ ev: "EmojiPopoverInline_render", canEdit: !!canEdit });
	const { darkMode } = useThemeContext();
	const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;
	const hoverBg = darkMode ? "hover:bg-[#374248]" : "hover:bg-gray-100";
	return (
		<div
			data-reaction-popover
			data-skip-react
			className="pointer-events-auto flex items-center gap-0.5 rounded-full border px-1.5 py-1 shadow-md"
			style={{ backgroundColor: tema.popoverBg, borderColor: tema.popoverBorder }}
		>
			{EMOJIS_PADROES.map((item) => (
				<button
					key={item.emoji}
					type="button"
					data-skip-react
					onClick={(e) => {
						dbgPush({ ev: "EmojiPopoverInline_onPick_quick", emoji: item.emoji, label: item.label });
						onPick(item.emoji);
					}}
					title={item.label}
					className={`rounded-full p-1 text-lg leading-none transition-transform hover:scale-125 ${hoverBg}`}
				>
					{item.emoji}
				</button>
			))}
			<div className="mx-1 h-4 w-px" style={{ backgroundColor: tema.popoverBorder }} />
			{canEdit && (
				<button
					type="button"
					data-skip-react
					title="Editar mensagem"
					onClick={(e) => {
						dbgPush({ ev: "EmojiPopoverInline_onEdit" });
						onEdit && onEdit();
					}}
					className={`rounded-full p-1 transition-colors ${hoverBg}`}
					style={{ color: tema.textoMuted }}
				>
					<Pencil className="h-4 w-4" />
				</button>
			)}
			<button
				type="button"
				data-skip-react
				title="Responder"
				onClick={(e) => {
					dbgPush({ ev: "EmojiPopoverInline_onReply" });
					onReply();
				}}
				className={`rounded-full p-1 transition-colors ${hoverBg}`}
				style={{ color: tema.textoMuted }}
			>
				<Reply className="h-4 w-4" />
			</button>
			<Popover>
				<PopoverTrigger asChild>
					<button
						type="button"
						data-skip-react
						title="Mais reações"
						className={`rounded-full p-1 transition-colors ${hoverBg}`}
						style={{ color: tema.textoMuted }}
						onClick={(e) => {
							dbgPush({ ev: "EmojiPopoverInline_smile_trigger_click" });
						}}
					>
						<Smile className="h-4 w-4" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					data-reaction-popover
					data-skip-react
					className="w-auto border-0 bg-transparent p-0 shadow-none"
					align="end"
					sideOffset={4}
					onOpenAutoFocus={(e) => e.preventDefault()}
					onPointerDownOutside={(e) => {
						const tgt = e.target;
						if (tgt instanceof Element && (tgt.closest?.("[data-reaction-wrapper], [data-reaction-popover]"))) {
							e.preventDefault();
						}
					}}
				>
					<div
						data-skip-react
					>
						<Picker
							data={emojiData}
							theme={darkMode ? "dark" : "light"}
							locale="pt"
							previewPosition="none"
							skinTonePosition="none"
							onEmojiSelect={(sel) => {
								if (sel?.native) {
									dbgPush({ ev: "EmojiPopoverInline_onPick_picker", emoji: sel.native });
									onPick(sel.native);
								}
							}}
						/>
					</div>
				</PopoverContent>
			</Popover>
			<div className="mx-0.5 h-4 w-px" style={{ backgroundColor: tema.popoverBorder }} />
			<button
				type="button"
				data-skip-react
				title="Fechar"
				onClick={(e) => {
					dbgPush({ ev: "EmojiPopoverInline_onClose" });
					onClose && onClose();
				}}
				className={`rounded-full p-1 transition-colors ${hoverBg}`}
				style={{ color: tema.textoMuted }}
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
};

const conversationKey = (conv) =>
	conv.type === "group" ? `group-${conv.groupId}` : `direct-${conv.userId}`;

const InternalChat = () => {
	const { user } = useContext(AuthContext);
	const { darkMode } = useThemeContext();
	const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;

	// Refs SINGLETON para listeners de efeito que rodam apenas 1x (StrictMode 2x
	// também protege). Mantem estado atual sem re-inscrever listener a cada
	// mudança de reactionPopoverFor ou messages (causa raiz do "popup duplicando
	// e nao funciona").
	const reactionPopoverForRef = useRef(null);
	const messagesRef = useRef([]);
	const userRef = useRef(user);
	const replyToMessageRef = useRef(() => {});
	// Evita listener inscrito 2x no mesmo root.
	const listenerNativeInstaladoRef = useRef(false);

	// O aviso sonoro do chat. Em ref porque quem o dispara é o ouvinte do
	// socket, montado uma vez: sem a ref ele guardaria a função da primeira
	// renderização.
	//
	// RACE FIX volume notificações: ref notifVolume01Ref é atualizada a CADA
	// render sempre com o valor mais novo; a função tocarAviso lê a ref NA
	// HORA do play, garantindo que 0% = silêncio real mesmo se o listener
	// do socket foi criado no paint inicial (fechamento de closure antiga
	// com valor 50%).
	//
	// BUG || 50: 0 || 50 === 50 (operador || do JS transforma 0 falsy em 50).
	// Isso era por que 0% = tocava normal (50% default). Resolvido com
	// checagem explícita undefined/null.
	const notifVolumeBruto =
		user && typeof user?.notificationVolume !== "undefined" && user?.notificationVolume !== null
			? Number(user.notificationVolume)
			: 50;
	const notifVolume01 = Math.max(0, Math.min(1, notifVolumeBruto / 100));
	const notifVolume01Ref = useRef(notifVolume01);
	// Atualiza sempre, sem condicional, durante render: valor MAIS NOVO
	// SEMPRE disponível para quem lê a ref.
	notifVolume01Ref.current = notifVolume01;

	const [tocar] = useSound(somDeAviso, { volume: notifVolume01 });
	const tocarRef = useRef(tocar);

	// A ref é atualizada em efeito, não durante o render: quem dispara o som é
	// o ouvinte do socket, montado uma vez, e sem isto ele guardaria a função
	// da primeira renderização.
	useEffect(() => {
		tocarRef.current = tocar;
	}, [tocar]);

	const tocarAviso = useCallback(() => {
		try {
			// HOWLER BUG PARANOICO FIX (threshold 1%):
			// Relato usuario: '1% sai mega baixo, 0% sai som NORMAL' — bug
			// conhecido use-sound/howler que ignora volume=0 exato.
			// Abaixo de 1% NÃO CHAMAMOS a função play() do use-sound
			// NENHUMA VEZ. Se a função não é invocada, não há forma de
			// ela tocar. Number.isFinite evita NaN vindo de refs.
			const vRaw = Number(notifVolume01Ref.current);
			const vPlay = Number.isFinite(vRaw)
				? Math.max(0, Math.min(1, vRaw))
				: 0;
			if (vPlay > 0.01) {
				tocarRef.current?.({ volume: vPlay });
			}
		} catch {
			// Navegador que ainda não teve interação bloqueia o áudio. A
			// mensagem continua chegando; só não apita.
		}
	}, []);

	const [conversations, setConversations] = useState([]);
	const [selected, setSelected] = useState(null);
	const [messages, setMessages] = useState([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [inputMessage, setInputMessage] = useState("");
	const [users, setUsers] = useState([]);
	const [medias, setMedias] = useState([]);
	const [quotedMessage, setQuotedMessage] = useState(null);
	const [reactionPopoverFor, setReactionPopoverFor] = useState(null);
	const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
	const [deletingGroup, setDeletingGroup] = useState(false);

	// Estado de posicionamento do popup de reações, renderizado em createPortal FORA do wrapper
	// da mensagem (para eventos do popup NÃO chegarem no onClick/dblclick do wrapper).
	const [popupRect, setPopupRect] = useState(null);

	const [editingMessageId, setEditingMessageId] = useState(null);
	const [editingBody, setEditingBody] = useState("");
	const [savingEdit, setSavingEdit] = useState(false);
	const [recording, setRecording] = useState(false);
	const [imagemModal, setImagemModal] = useState(null); // { url, nome } | null
	const [visualizando, setVisualizando] = useState(null); // índice em arquivosDaConversa | null
	const [transcrevendoIds, setTranscrevendoIds] = useState([]); // ids de mensagens sendo transcritas

	// ================================================================
	// (H) Busca no chat interno (mensagens) — IGUAL WhatsApp/WIDGET
	// ================================================================
	const [buscaMsg, setBuscaMsg] = useState("");
	const [buscaAberta, setBuscaAberta] = useState(false);
	const [indiceMatch, setIndiceMatch] = useState(0);

	// IDs filtrados por buscaMsg (calculado e memoizado durante render).
	// idsMatchesRef é salvo em ref para buscaMsgInputRef next/prev nao precisar
	// de dependencia que force re-render do botao.
	const idsMatchesBusca = useMemo(() => {
		const q = String(buscaMsg || "").trim().toLowerCase();
		if (!q) return [];
		const ids = [];
		for (const m of messages) {
			const corpo = String(m.body || "").toLowerCase();
			const citou =
				m.quotedMsg &&
				String(m.quotedMsg.body || "").toLowerCase().includes(q);
			if (corpo.includes(q) || citou) ids.push(m.id);
		}
		return ids;
	}, [buscaMsg, messages]);

	const toggleBusca = useCallback(() => {
		setBuscaAberta((a) => {
			const nova = !a;
			if (!nova) {
				setBuscaMsg("");
				setIndiceMatch(0);
			} else {
				// foca o input no proximo frame
				setTimeout(() => {
					const el = document.querySelector("[data-busca-msg-interna-pagina]");
					if (el && typeof el.focus === "function") el.focus();
				}, 0);
			}
			return nova;
		});
	}, []);

	// ================================================================
	// (I) Figurinhas no chat interno (igual ticket / igual widget)
	// ================================================================
	const [figurinhaAberto, setFigurinhaAberto] = useState(false);
	const figurinhaAncoraRef = useRef(null);

	// Helpers para upload figurinha como midia no chat interno (direto OU grupo).
	// Mesmo padrão do widget InternalChatWidget + enviarMensagem page abaixo.
	const pegarRotaEnvioPage = useCallback(() => {
		if (!selected) return null;
		if (selected.type === "group")
			return `/internal-chat/groups/${selected.id}/messages`;
		return `/internal-chat/direct/${selected.id}/messages`;
	}, [selected]);

	// Função de envio customizada da página InternalChat para figurinhas.
	// IGUAL ao envio de figurinha de TICKETS (FigurinhaPicker fluxo original,
	// que faz POST /figurinhas/:id/enviar com {ticketId, quotedMsg}), só que
	// usa a rota DEDICADA do backend para chat interno:
	//   POST /internal-chat/direct/:userId/figurinhas/:id/enviar  (direta)
	//   POST /internal-chat/groups/:id/figurinhas/:itemId/enviar  (grupo)
	//
	// Isso NÃO envolve fetch blob, conversão para File, upload multipart,
	// CORS same-origin hacks, nada — o backend COPIA a figurinha salva e grava
	// direto na InternalChatMessage, 100x mais simples e sem falhas em produção.
	//
	// Retorno: true em sucesso, false em erro (igual ao fluxo de tickets).
	// Mesmo se erro retornar false, o FigurinhaPicker commit a630f93 NÃO cai
	// mais em fallback de rota tickets (return no bloco custom).
	const enviarFigurinhaPagina = useCallback(
		async (figurinha, ctx) => {
			if (!selected || !figurinha?.id) return false;
			try {
				const ehGrupo = selected.type === "group";
				// selected.type = "group" → selected.groupId é o ID real do grupo,
				// selected.id seria o userId (undefined). Sempre usar o campo
				// correto para 404 não acontecer.
				const idConversa = ehGrupo ? selected.groupId : selected.userId || selected.id;
				const rota = ehGrupo
					? `/internal-chat/groups/${idConversa}/figurinhas/${figurinha.id}/enviar`
					: `/internal-chat/direct/${idConversa}/figurinhas/${figurinha.id}/enviar`;

				const quotedMsg = ctx?.quotedMsg || quotedMessage || null;
				const quotedMsgId =
					quotedMsg && quotedMsg.id ? String(quotedMsg.id) : undefined;

				const tempId = `tmp_${Date.now()}`;
				const temporaria = {
					__tempId: tempId,
					id: -Math.floor(Math.random() * 1e9),
					body: "",
					mediaUrl: getPublicFileUrl(figurinha.mediaUrl) || "",
					mediaType: "sticker",
					fromUserId: Number(user?.id),
					toUserId: ehGrupo ? null : Number(idConversa),
					groupId: ehGrupo ? Number(idConversa) : null,
					createdAt: new Date().toISOString(),
					fromUser: {
						id: Number(user?.id),
						name: user?.name || "Eu",
						profilePicUrl: user?.profilePicUrl || null,
					},
					quotedMsg:
						quotedMsg && quotedMsg.id
							? {
									id: quotedMsg.id,
									fromUserId: quotedMsg.fromUserId,
									fromUser: quotedMsg.fromUser,
									body: quotedMsg.body,
									mediaUrl: quotedMsg.mediaUrl,
									mediaType: quotedMsg.mediaType,
								}
							: null,
				};
				setMessages((m) => [...m, temporaria]);
				setTimeout(() => {
					scrollListRef.current?.scrollTo?.({
						top: scrollListRef.current.scrollHeight,
						behavior: "smooth",
					});
				}, 0);
				tocarAviso();

				const payload = {};
				if (quotedMsgId !== undefined) payload.quotedMsgId = quotedMsgId;

				const { data } = await api.post(rota, payload);
				const enviadas = Array.isArray(data) ? data : [data];
				setMessages((m) => {
					const semTemp = m.filter((x) => x.__tempId !== tempId);
					return [...semTemp, ...enviadas];
				});
				setQuotedMessage(null);
				return true;
			} catch (err) {
				toastError(err);
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selected?.id, selected?.type, quotedMessage, user]
	);

	const [aiAberto, setAiAberto] = useState(false);
	const [aiModo, setAiModo] = useState("corrigir");
	const [aiCarregando, setAiCarregando] = useState("");
	const [aiErro, setAiErro] = useState("");
	const [aiVersoes, setAiVersoes] = useState([]);
	const [aiSugestoes, setAiSugestoes] = useState([]);
	const TONS_IA = [
		{ acao: "corrigir", rotulo: "Corrigido" },
		{ acao: "melhorar", rotulo: "Mais claro" },
		{ acao: "formal", rotulo: "Mais formal" },
		{ acao: "informal", rotulo: "Mais informal" },
		{ acao: "resumir", rotulo: "Mais curto" },
	];

	const fileInputRef = useRef(null);
	const gravadorRef = useRef(null);
	const pedacosRef = useRef([]);
	const singleClickTimer = useRef(null);
	const scrollListRef = useRef(null);
	const balaoRefs = useRef(new Map());
	const balaoRefsSet = (id, val) => {
		if (val) balaoRefs.current.set(id, val);
		else balaoRefs.current.delete(id);
	};

	const [groupDialogOpen, setGroupDialogOpen] = useState(false);
	const [groupName, setGroupName] = useState("");
	const [groupMemberIds, setGroupMemberIds] = useState([]);

	const [dmDialogOpen, setDmDialogOpen] = useState(false);
	const [dmUserId, setDmUserId] = useState("");

	const messagesEndRef = useRef(null);
	const selectedRef = useRef(null);
	selectedRef.current = selected;

	const editingMessageIdRef = useRef(null);
	editingMessageIdRef.current = editingMessageId;

	// Sincroniza Refs usadas pelos listeners singleton. Assim os listeners
	// (montados 1x, sem re-inscrição) sempre veem o estado mais recente.
	reactionPopoverForRef.current = reactionPopoverFor;
	messagesRef.current = messages;
	userRef.current = user;

	const arquivosDaConversa = messages
		.filter((m) => m.mediaUrl && !m.isDeleted)
		.map((m) => ({
			id: m.id,
			url: getPublicFileUrl(m.mediaUrl),
			mediaType: m.mediaType,
			legenda: m.body || "",
			fromMe: m.fromMe,
			createdAt: m.createdAt,
		}));

	const abrirArquivo = (messageId) => {
		const indice = arquivosDaConversa.findIndex((a) => a.id === messageId);
		if (indice >= 0) setVisualizando(indice);
	};

	const scrollToBottom = () => {
		setTimeout(() => {
			messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
		}, 100);
	};

	const fetchConversations = useCallback(async () => {
		try {
			const { data } = await api.get("/internal-chat/conversations");
			// Dedup por chave de conversa (group-N / direct-N). Evita a exibição
			// de múltiplas linhas para a MESMA conversa se a API retornar
			// duplicatas (ex: join errado no ListConversationsService ao incluir
			// attachments/mídias múltiplas) ou se houver múltiplas chamadas
			// concorrentes fetchConversations() vindas de socket events.
			const map = new Map();
			const arr = Array.isArray(data) ? data : [];
			for (const c of arr) {
				const k = conversationKey(c);
				if (!k || map.has(k)) continue;
				map.set(k, c);
			}
			setConversations(Array.from(map.values()));
		} catch (err) {
			toastError(err);
		}
	}, []);

	const handleTranscrever = useCallback(async (msg, opts = {}) => {
		const { forcar = false } = opts;
		if (!msg?.id) return;
		const id = +msg.id;
		setTranscrevendoIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
		try {
			const { data } = await api.post(`/internal-chat/messages/${id}/transcribe`, { forcar: !!forcar });
			setMessages((prev) =>
				prev.map((m) =>
					+m.id === id
						? {
								...m,
								transcription:
									typeof data?.transcription === "string"
										? data.transcription
										: m.transcription ?? "",
								transcribedAt: data?.transcribedAt || m.transcribedAt || new Date().toISOString(),
							}
						: m
				)
			);
		} catch (err) {
			toastError(err);
		} finally {
			setTranscrevendoIds((prev) => prev.filter((x) => +x !== +id));
		}
	}, []);

	// Reposiciona o popup de reações via createPortal (fora do wrapper da msg).
	// Como ele é um irmão direto do body, eventos de lá NUNCA vão chegar no onClick do
	// wrapper do balão (causa raiz de "no popup não funcionam as reações").
	const popupHostRef = useRef(null);
	const popupAbertoMsg =
		reactionPopoverFor != null ? messages.find((m) => +m.id === +reactionPopoverFor) : null;

	useLayoutEffect(() => {
		if (reactionPopoverFor === null) {
			setPopupRect(null);
			return;
		}
		const calcular = () => {
			const item = balaoRefs.current.get(reactionPopoverFor);
			if (!item || !item.el) {
				setPopupRect(null);
				return;
			}
			const r = item.el.getBoundingClientRect();
			setPopupRect({
				top: r.top,
				left: r.left,
				width: r.width,
				height: r.height,
				isMine: !!item.isMine,
				msgId: reactionPopoverFor
			});
		};
		calcular();
		const scrollEl = scrollListRef.current;
		scrollEl?.addEventListener("scroll", calcular, { passive: true });
		window.addEventListener("resize", calcular);
		return () => {
			scrollEl?.removeEventListener("scroll", calcular);
			window.removeEventListener("resize", calcular);
		};
	}, [reactionPopoverFor, messages.length, selected]);

	// Fechamento do popup de reações: NÃO TEM MAIS listeners agressivos de document mousedown/click.
	// Popup fecha apenas por 4 gatilhos determinísticos:
	// 1. Botão X (Fechar) no próprio popup (EmojiPopoverInline onClose)
	// 2. ESC pressionado (keydown document acima)
	// 3. Clicar em outra mensagem (listener singleton do scrollList abre popup diferente)
	// 4. Ação concluída com sucesso (toggleReaction / replyToMessage / startEditMessage)
	// Com isso: zero chance de fechar o popup ANTES do onClick dos botões, que era root cause histórico.

	useEffect(() => {
		fetchConversations();
	}, [fetchConversations]);

	useEffect(() => {
		if (reactionPopoverFor === null) return;
		const handler = (ev) => {
			if (ev.key === "Escape") {
				dbgPush({ ev: "fechar_popup_esc" });
				setReactionPopoverFor(null);
			}
		};
		document.addEventListener("keydown", handler, { capture: false });
		return () => document.removeEventListener("keydown", handler, { capture: false });
	}, [reactionPopoverFor]);

	useEffect(() => {
		const host = popupHostRef.current;
		if (!host) return;
		const handlerClick = (ev) => {
			dbgPush({
				ev: "portal_native_stop",
				tipo: ev.type,
				tag: (ev.target instanceof Element) ? ev.target.tagName : "not_element",
				phase: ev.eventPhase,
			});
		};
		host.addEventListener("click", handlerClick, { capture: false });
		return () => {
			host.removeEventListener("click", handlerClick, { capture: false });
		};
	}, [reactionPopoverFor, popupRect]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/users");
				setUsers(data.users || []);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	const markAsRead = useCallback(async (conv) => {
		const id = conv.type === "group" ? conv.groupId : conv.userId;
		try {
			await api.post(`/internal-chat/${id}/read?type=${conv.type}`);
		} catch (err) {
			// silencioso — não bloquear a UI por falha ao marcar como lido
		}
		setConversations((prev) =>
			prev.map((c) =>
				conversationKey(c) === conversationKey(conv)
					? { ...c, unreadCount: 0 }
					: c
			)
		);
	}, []);

	const handleSelectConversation = useCallback(
		async (conv) => {
			setSelected(conv);
			setMessages([]);
			setLoadingMessages(true);
			try {
				const url =
					conv.type === "group"
						? `/internal-chat/groups/${conv.groupId}/messages`
						: `/internal-chat/direct/${conv.userId}/messages`;
				const { data } = await api.get(url, {
					params: { pageSize: 100 },
				});
				setMessages(data.messages || []);
				scrollToBottom();
			} catch (err) {
				toastError(err);
			}
			setLoadingMessages(false);
			markAsRead(conv);
		},
		[markAsRead]
	);

	const belongsToSelected = useCallback(
		(message, conv) => {
			if (!conv) return false;
			if (conv.type === "group") {
				return message.groupId === conv.groupId;
			}
			return (
				!message.groupId &&
				((message.fromUserId === conv.userId && message.toUserId === user?.id) ||
					(message.fromUserId === user?.id && message.toUserId === conv.userId))
			);
		},
		[user]
	);

	useEffect(() => {
		const socket = openSocket();
		// #region debug-point popup-duplicate
		const sid = String(Math.random()).slice(2, 8);
		dbgPush({ ev: "socket_effect_setup", sid, hasUser: !!userRef.current, hasSel: !!selectedRef.current });
		// #endregion

		const pertenceAConversa = (message, conv) => {
			if (!conv) return false;
			if (conv.type === "group") return message.groupId === conv.groupId;
			const userLocal = userRef.current;
			return (
				!message.groupId &&
				((message.fromUserId === conv.userId && message.toUserId === userLocal?.id) ||
					(message.fromUserId === userLocal?.id && message.toUserId === conv.userId))
			);
		};

		const onMessage = (data) => {
			const message = data.message;
			if (!message) return;
			const userLocal = userRef.current;
			const euEnviei = +message.fromUserId === +userLocal?.id;
			dbgPush({
				ev: "socket_message",
				sid,
				msgId: String(message.id),
				fromUserId: String(message.fromUserId),
				toUserId: String(message.toUserId || "null"),
				groupId: String(message.groupId || "null"),
				userId: String(userLocal?.id || "null"),
				euEnviei: !!euEnviei,
				body_preview: String(message.body || "").slice(0, 40),
			});

			if (!euEnviei) tocarAviso();
			const conv = selectedRef.current;
			if (pertenceAConversa(message, conv)) {
				setMessages((prev) => {
					const jaExiste = prev.some((m) => +m.id === +message.id);
					dbgPush({
						ev: "socket_message_setMessages",
						sid,
						msgId: String(message.id),
						jaExiste: !!jaExiste,
						euEnviei: !!euEnviei,
						prevLen: prev.length,
						resultado: jaExiste ? "skip" : "append",
					});
					// NÃO SKIPAMOS se euEnviei: a mensagem pode ter sido enviada
					// por outra aba do mesmo usuário (ex: widget vs página), ou
					// a resposta HTTP chegou parcial e a temporária foi substituída
					// mas a versão definitiva veio via socket. Apenas dedup por id.
					if (jaExiste) return prev;
					return [...prev, message];
				});
				if (!euEnviei) {
					scrollToBottom();
					markAsRead(conv);
				}
			}
			fetchConversations();
		};

		const onReaction = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMessages((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (m.id !== payload.messageId) return m;
					mudou = true;
					return { ...m, reacoes: Array.isArray(payload.reacoes) ? payload.reacoes : [] };
				});
				return mudou ? novas : prev;
			});
		};

		const onFavorite = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMessages((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (m.id !== payload.messageId) return m;
					mudou = true;
					return { ...m, favorita: !!payload.favorita };
				});
				return mudou ? novas : prev;
			});
		};

		const onMessageEdit = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMessages((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (m.id !== payload.messageId) return m;
					mudou = true;
					return {
						...m,
						body: typeof payload.body === "string" ? payload.body : m.body,
						editedAt: payload.editedAt || m.editedAt || new Date().toISOString(),
						reacoes: Array.isArray(payload.reacoes) ? payload.reacoes : m.reacoes,
						transcription:
							typeof payload.transcription === "string"
								? payload.transcription
								: m.transcription,
						transcribedAt: payload.transcribedAt || m.transcribedAt || null,
					};
				});
				return mudou ? novas : prev;
			});
			if (typeof payload.messageId === "number" && +payload.messageId === +editingMessageIdRef.current) {
				setEditingMessageId(null);
				setEditingBody("");
				setSavingEdit(false);
			}
		};

		const onGroup = (payload) => {
			fetchConversations();
			if (payload?.action === "delete" && typeof payload.groupId === "number") {
				const conv = selectedRef.current;
				if (conv?.type === "group" && +conv.groupId === +payload.groupId) {
					setSelected(null);
					setMessages([]);
				}
			}
		};

		socket.on("internalChat:message", onMessage);
		socket.on("internalChat:reaction", onReaction);
		socket.on("internalChat:favorite", onFavorite);
		socket.on("internalChat:messageEdit", onMessageEdit);
		socket.on("internalChat:group", onGroup);

		return () => {
			try { socket.off("internalChat:message", onMessage); } catch (_) {}
			try { socket.off("internalChat:reaction", onReaction); } catch (_) {}
			try { socket.off("internalChat:favorite", onFavorite); } catch (_) {}
			try { socket.off("internalChat:messageEdit", onMessageEdit); } catch (_) {}
			try { socket.off("internalChat:group", onGroup); } catch (_) {}
		};
	}, [fetchConversations, markAsRead]);

	const replyToMessage = (message) => {
		dbgPush({ ev: "replyToMessage", messageId: String(message?.id) });
		setReactionPopoverFor(null);
		setQuotedMessage(message);
		setTimeout(() => document.querySelector("[data-internal-chat-input]")?.focus?.(), 50);
	};
	replyToMessageRef.current = replyToMessage;

	const cancelReply = () => {
		setQuotedMessage(null);
	};

	const toggleReaction = async (messageId, emoji) => {
		dbgPush({ ev: "toggleReaction_START", messageId: String(messageId), emoji });
		const mid = Number(messageId);
		let stateAnterior = null;
		try {
			if (!messageId || !emoji) return;
			stateAnterior = messagesRef.current.map(m => +m.id === mid ? { ...m, reacoes: Array.isArray(m.reacoes) ? m.reacoes.map(r=>({...r})) : [] } : m);
			const userIdAtual = user?.id;
			const agregaReacoes = (reacoesExistentes) => {
				const arr = Array.isArray(reacoesExistentes) ? reacoesExistentes : [];
				const agrupado = new Map();
				arr.forEach((r) => {
					if (!r || !r.emoji) return;
					const e = String(r.emoji);
					const users = Array.isArray(r.userIds)
						? r.userIds.map(String)
						: r.userId != null
						? [String(r.userId)]
						: [];
					const atual = agrupado.get(e);
					if (atual) {
						users.forEach((u) => { if (!atual.userIds.includes(u)) atual.userIds.push(u); });
					} else {
						agrupado.set(e, { emoji: e, total: 0, userIds: users });
					}
				});
				const saida = [];
				agrupado.forEach((v, e) => { saida.push({ emoji: e, total: v.userIds.length, userIds: v.userIds }); });
				return saida;
			};
			const toggleAgg = (lista, emj, uid) => {
				const agr = agregaReacoes(lista);
				const idx = agr.findIndex((x) => x.emoji === emj);
				if (idx >= 0) {
					const linha = agr[idx];
					const jaTem = linha.userIds.includes(String(uid));
					if (jaTem) {
						linha.userIds = linha.userIds.filter((u) => u !== String(uid));
						linha.total = linha.userIds.length;
						if (linha.total <= 0) agr.splice(idx, 1);
					} else {
						linha.userIds.push(String(uid));
						linha.total = linha.userIds.length;
					}
				} else {
					agr.push({ emoji: emj, total: 1, userIds: [String(uid)] });
				}
				return agr.map((r)=>({...r, _t: Date.now()}));
			};
			if (userIdAtual != null) {
				setMessages((prev) =>
					prev.map((m) =>
						+m.id === mid
							? { ...m, reacoes: toggleAgg(m.reacoes, emoji, userIdAtual) }
							: m
					)
				);
			}
			const { data } = await api.post(`/internal-chat/messages/${messageId}/reactions`, { emoji });
			dbgPush({ ev: "toggleReaction_SUCCESS", messageId: String(messageId), emoji, data: !!data });
			if (data && Array.isArray(data.reacoes)) {
				setMessages((prev) =>
					prev.map((m) =>
						+m.id === mid
							? { ...m, reacoes: data.reacoes.map((r) => ({ ...r, _t: Date.now() })) }
							: m
					)
				);
			}
		} catch (err) {
			dbgPush({ ev: "toggleReaction_ERROR", messageId: String(messageId), emoji, msg: err?.message || String(err).slice(0, 120) });
			if (stateAnterior) setMessages(() => stateAnterior);
			toastError(err);
		} finally {
			dbgPush({
				ev: "toggleReaction_END_CLOSE_POPUP_NOW",
				beforeRef: String(reactionPopoverForRef.current || "null"),
				beforeState: String(reactionPopoverFor || "null"),
				mid: String(mid),
			});
			reactionPopoverForRef.current = null;
			setReactionPopoverFor(null);
		}
	};

	const podeEditarMensagem = (message) => {
		if (!message || !user) return false;
		const profile = String(user?.profile || "user").toLowerCase();
		const ehAdminOuSupervisor =
			profile === "admin" || profile === "administrator" || profile === "supervisor";
		const souDono = +message.fromUserId === +user.id;
		return souDono || ehAdminOuSupervisor;
	};

	const startEditMessage = (message) => {
		dbgPush({ ev: "startEditMessage", messageId: String(message?.id), bodyPreview: String(message?.body || "").slice(0, 60) });
		setReactionPopoverFor(null);
		setEditingMessageId(message.id);
		setEditingBody(message.body == null ? "" : String(message.body));
	};

	const cancelEditMessage = () => {
		setEditingMessageId(null);
		setEditingBody("");
		setSavingEdit(false);
	};

	const saveEditMessage = async () => {
		if (editingMessageId === null || savingEdit) return;
		try {
			setSavingEdit(true);
			await api.patch(`/internal-chat/messages/${editingMessageId}`, { body: editingBody });
			toast.success("Mensagem editada.");
			cancelEditMessage();
		} catch (err) {
			toastError(err);
			setSavingEdit(false);
		}
	};

	// NATIVO: delega cliques nos wrappers de mensagem via eventListener no scrollListRef.
	// Evita 100% os conflitos entre synthetic events do React, stopPropagation nativo e
	// composedPath — que eram a causa raiz do popup de reações NUNCA abrir.
	//
	// Listener SINGLETON: roda uma vez (por root DOM e montagem do componente). As deps
	// são [] e ele lê estado atual via refs (reactionPopoverForRef, messagesRef,
	// replyToMessageRef). Isso evita o bug principal: NÃO reinscreve o listener toda vez
	// que setReactionPopoverFor ou messages mudam.
	//
	// Regras de interação (combinam dinâmica do WhatsApp):
	//   • 2 cliques rápidos (< 280ms) no MESMO balão → RESPONDE inline (quoted reply).
	//   • 1 CLIQUE BOTÃO DIREITO (contextmenu) no balão → ABRE popup de ações
	//     (reações rápidas 👍❤️😂😮😢🙏🔥🎉 + Smile picker completo + Editar + Responder + X).
	//   • 1 clique ESQUERDO normal → NÃO abre nada; só guarda lastTs/lastId para detectar
	//     dblclick no próximo clique (igual WhatsApp mobile/web).
	//   • Clique em a/button/input/img/video/[data-skip-react] ou dentro do popup de ações → ignora.
	const msgClickState = useRef({
		lastTs: 0,
		lastId: null,
		pendingOpen: null
	});

	useEffect(() => {
		let cancelled = false;
		let observer = null;
		let timers = [];

		const achaWrapper = (el) => el instanceof Element && el.closest?.("[data-reaction-wrapper]");
		const achaMessageId = (wrapper) => {
			const li = wrapper.closest("[data-msg-id]");
			if (!li) return null;
			const val = li.getAttribute("data-msg-id");
			const n = Number(val);
			return Number.isFinite(n) && n > 0 ? n : null;
		};
		const isCliqueEmElementoInterativo = (el) =>
			el instanceof Element &&
			el.closest(
				"a,button,input,textarea,img,audio,video,[data-skip-react],em-emoji,[data-reaction-popover], [data-radix-popper-content-wrapper]"
			);
		const estaDentroDoPopPortal = (el) =>
			!!(popupHostRef.current && popupHostRef.current.contains?.(el));

		const onNativeClick = (ev) => {
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (isCliqueEmElementoInterativo(t) || estaDentroDoPopPortal(t)) return;
			const wrapper = achaWrapper(t);
			if (!wrapper) return;
			const msgId = achaMessageId(wrapper);
			if (msgId == null) return;
			const agora = Date.now();
			const st = msgClickState.current;
			const dt = agora - (st.lastTs || 0);
			dbgPush({
				ev: "native_click",
				msgId: String(msgId),
				dt: dt,
				lastId: String(st.lastId || "null"),
				before: String(reactionPopoverForRef.current || "null"),
				selectedId: String(selected?.id || "null"),
			});
			if (st.lastId === msgId && dt < 280) {
				st.lastTs = 0;
				st.lastId = null;
				if (st.pendingOpen) {
					clearTimeout(st.pendingOpen);
					st.pendingOpen = null;
				}
				const msg = messagesRef.current.find((m) => +m.id === +msgId);
				if (msg) {
					replyToMessageRef.current?.(msg);
				}
				return;
			}
			st.lastTs = agora;
			st.lastId = msgId;
		};

		const onNativeContextMenu = (ev) => {
			ev.preventDefault();
			try { ev.stopImmediatePropagation(); } catch {}
			try { ev.stopPropagation(); } catch {}
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (estaDentroDoPopPortal(t)) return;
			const wrapper = achaWrapper(t);
			if (!wrapper) return;
			const msgId = achaMessageId(wrapper);
			if (msgId == null) return;
			if (isCliqueEmElementoInterativo(t)) return;
			dbgPush({
				ev: "native_contextmenu_abre_popup",
				msgId: String(msgId),
				before: String(reactionPopoverForRef.current || "null"),
				selectedId: String(selected?.id || "null"),
			});
			setReactionPopoverFor((atual) =>
				+atual === +msgId ? null : msgId
			);
		};

		const onNativeDblClick = (ev) => {
			const path = typeof ev.composedPath === "function" ? ev.composedPath() : [];
			const pathTemElementoBloqueado = path.some(function (node) {
				if (!(node instanceof Element)) return false;
				return node.matches(
					"[data-skip-react], [data-reaction-popover], [data-popup-host-internal], [data-popup-host-internal-chat-widget], [data-radix-popper-content-wrapper], button, a, input, textarea, img, audio, video"
				);
			});
			if (pathTemElementoBloqueado) return;
			const t = ev.target;
			if (isCliqueEmElementoInterativo(t) || estaDentroDoPopPortal(t)) return;
			const wrapper = achaWrapper(t);
			if (!wrapper) return;
			const msgId = achaMessageId(wrapper);
			if (msgId == null) return;
			dbgPush({ ev: "native_dblclick", msgId: String(msgId) });
			const msg = messagesRef.current.find((m) => +m.id === +msgId);
			if (msg) {
				replyToMessageRef.current?.(msg);
			}
			msgClickState.current = { lastTs: 0, lastId: null, pendingOpen: null };
		};

		let rootUsado = null;

		const instalaListeners = (root) => {
			if (cancelled || !root || rootUsado === root) return;
			rootUsado = root;

			dbgPush({
				ev: "listener_effect_setup_ONLY_ONCE",
				root: !!root,
				selectedId: String(selected?.id || "null"),
			});
			listenerNativeInstaladoRef.current = true;

			root.addEventListener("click", onNativeClick, { passive: true });
			root.addEventListener("contextmenu", onNativeContextMenu, { passive: false });
			root.addEventListener("dblclick", onNativeDblClick, { passive: true });
		};

		const esperaInstala = () => {
			if (cancelled) return;
			const r = scrollListRef.current;
			if (r) { instalaListeners(r); return; }
			let tentativas = 0;
			const tenta = () => {
				if (cancelled) return;
				tentativas++;
				const rt = scrollListRef.current;
				if (rt) { instalaListeners(rt); return; }
				if (tentativas < 5) {
					const id = setTimeout(tenta, 50);
					timers.push(id);
					return;
				}
				try {
					observer = new MutationObserver(() => {
						if (cancelled && observer) { observer.disconnect(); return; }
						const rr = scrollListRef.current;
						if (rr && observer) { observer.disconnect(); instalaListeners(rr); }
					});
					observer.observe(document.body, { childList: true, subtree: true });
				} catch (e) { /* noop */ }
			};
			tenta();
		};
		esperaInstala();

		return () => {
			cancelled = true;
			timers.forEach(id => clearTimeout(id));
			timers = [];
			if (observer) { try { observer.disconnect(); } catch (e) {} observer = null; }
			const rf = rootUsado || scrollListRef.current;
			if (rf) {
				dbgPush({
					ev: "listener_effect_cleanup",
					selectedId: String(selected?.id || "null"),
				});
				rf.removeEventListener("click", onNativeClick);
				rf.removeEventListener("contextmenu", onNativeContextMenu);
				rf.removeEventListener("dblclick", onNativeDblClick);
			}
			listenerNativeInstaladoRef.current = false;
			rootUsado = null;
		};
	}, [selected?.id]);

	const confirmDeleteGroup = async () => {
		if (!selected || selected.type !== "group") return;
		try {
			setDeletingGroup(true);
			await api.delete(`/internal-chat/groups/${selected.groupId}`);
			toast.success("Grupo excluído com sucesso.");
			setDeleteGroupDialogOpen(false);
			setSelected(null);
			setMessages([]);
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingGroup(false);
		}
	};

	const handlePickFiles = () => {
		fileInputRef.current?.click();
	};

	const arquivosValidosDoClipboard = (filesEntrada) => {
		const files = Array.from(filesEntrada || []);
		if (!files.length) return [];
		const invalidos = files.filter((f) => {
			const ext = extensaoDe(f.name);
			return !EXTENSOES_PERMITIDAS.includes(ext);
		});
		if (invalidos.length > 0) {
			toast.error(
				`Formato(s) não permitido(s) no colar: ${invalidos
					.map((f) => extensaoDe(f.name) || f.name)
					.filter(Boolean)
					.slice(0, 3)
					.join(", ")}`,
			);
		}
		return files.filter((f) =>
			EXTENSOES_PERMITIDAS.includes(extensaoDe(f.name)),
		);
	};

	const handleFilesChanged = (e) => {
		const permitidos = arquivosValidosDoClipboard(e.target.files);
		if (permitidos.length > 0) {
			setMedias((prev) => [...prev, ...permitidos]);
		}
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleStartRecord = async () => {
		if (recording) return;
		const formato = escolherFormatoAudio();
		if (!formato) {
			toast.error("Este navegador não suporta gravação de áudio.");
			return;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const gravador = formato.mime
				? new MediaRecorder(stream, { mimeType: formato.mime })
				: new MediaRecorder(stream);
			pedacosRef.current = [];
			gravadorRef.current = { recorder: gravador, stream, ext: formato.ext };
			gravador.ondataavailable = (evento) => {
				if (evento.data && evento.data.size > 0) pedacosRef.current.push(evento.data);
			};
			gravador.start(1000);
			setRecording(true);
		} catch (err) {
			if (err?.name === "NotAllowedError") {
				toast.error("Você bloqueou o microfone. Libere no cadeado ao lado do endereço.");
			} else if (err?.name === "NotFoundError") {
				toast.error("Nenhum microfone encontrado.");
			} else {
				toastError(err);
			}
		}
	};

	const encerrarGravacao = () =>
		new Promise((resolve) => {
			const atual = gravadorRef.current;
			if (!atual) { resolve(null); return; }
			const finalizar = () => {
				atual.stream.getTracks().forEach((t) => t.stop());
				gravadorRef.current = null;
				const blob = new Blob(pedacosRef.current, { type: atual.recorder.mimeType || "audio/webm" });
				pedacosRef.current = [];
				resolve({ blob, ext: atual.ext });
			};
			if (atual.recorder.state === "inactive") { finalizar(); return; }
			atual.recorder.onstop = finalizar;
			atual.recorder.stop();
		});

	const handleUploadAudio = async () => {
		try {
			const gravado = await encerrarGravacao();
			setRecording(false);
			if (!gravado) return;
			if (gravado.blob.size < 2000) {
				toast.info("Gravação muito curta. Segure o botão enquanto fala.");
				return;
			}
			if (!selected) return;
			const wav = await paraWav(gravado.blob);
			const formData = new FormData();
			const filename = `${new Date().getTime()}.wav`;
			formData.append("medias", wav, filename);
			formData.append("body", "");
			const url =
				selected.type === "group"
					? `/internal-chat/groups/${selected.groupId}/messages`
					: `/internal-chat/direct/${selected.userId}/messages`;
			await api.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
			fetchConversations();
		} catch (err) {
			toastError(err);
			setRecording(false);
		}
	};

	const handleCancelAudio = async () => {
		await encerrarGravacao();
		setRecording(false);
	};

	const handlePaste = (e) => {
		// Evita perder o evento: input de texto copiado normal passa direto.
		const cboard = e.clipboardData || window.clipboardData;
		if (!cboard) return;

		let arquivosColados = [];
		try {
			arquivosColados = Array.from(cboard.files || []);
		} catch (err) {
			arquivosColados = [];
		}

		// Navegadores às vezes trazem itens como File dentro de items, não
		// em files (ex: colar screenshot do clipboard no macOS/Safari).
		if (arquivosColados.length === 0 && cboard.items) {
			const arr = [];
			for (let i = 0; i < cboard.items.length; i++) {
				const it = cboard.items[i];
				if (it && it.kind === "file") {
					const arq = it.getAsFile && it.getAsFile();
					if (arq) arr.push(arq);
				}
			}
			arquivosColados = arr;
		}
		if (!arquivosColados.length) return;

		const permitidos = arquivosValidosDoClipboard(arquivosColados);
		if (permitidos.length > 0) {
			e.preventDefault();
			setMedias((prev) => [...prev, ...permitidos]);
		}
	};

	const handleRemoveMedia = (idx) => {
		setMedias((prev) => prev.filter((_, i) => i !== idx));
	};

	const handleSendMessage = async () => {
		const body = inputMessage.trim();
		const temMidia = medias.length > 0;
		if ((!body && !temMidia) || !selected) return;

		const url =
			selected.type === "group"
				? `/internal-chat/groups/${selected.groupId}/messages`
				: `/internal-chat/direct/${selected.userId}/messages`;

		const quotedMsgId = quotedMessage?.id || null;

		let data;

		try {
			if (temMidia) {
				const formData = new FormData();
				formData.append("body", body);
				if (quotedMsgId) formData.append("quotedMsgId", String(quotedMsgId));
				medias.forEach((arquivo) => {
					formData.append("medias", arquivo, arquivo.name);
				});

				const response = await api.post(url, formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});
				data = response.data;
			} else {
				const response = await api.post(url, {
					body,
					quotedMsgId,
				});
				data = response.data;
			}
		} catch (err) {
			toastError(err);
			return;
		}

		setInputMessage("");
		setMedias([]);
		setQuotedMessage(null);

		const recebidas = Array.isArray(data) ? data : [data];
		setMessages((prev) => {
			const idsPrev = new Set(prev.map((m) => +m.id));
			const adicionar = recebidas.filter((m) => !idsPrev.has(+m.id));
			// #region debug-point popup-duplicate
			dbgPush({
				ev: "http_send_setMessages",
				idsRecebidas: recebidas.map((m) => String(m.id)),
				idsAdicionadas: adicionar.map((m) => String(m.id)),
				prevLen: prev.length,
				newLen: prev.length + adicionar.length,
				adicionarLen: adicionar.length,
			});
			// #endregion
			if (adicionar.length === 0) return prev;
			return [...prev, ...adicionar];
		});

		scrollToBottom();
		fetchConversations();
	};

	const handleCreateGroup = async () => {
		if (!groupName.trim()) return;
		try {
			const { data } = await api.post("/internal-chat/groups", {
				name: groupName.trim(),
				memberIds: groupMemberIds,
			});
			toast.success("Grupo criado com sucesso.");
			setGroupDialogOpen(false);
			setGroupName("");
			setGroupMemberIds([]);
			await fetchConversations();
			handleSelectConversation({
				type: "group",
				groupId: data.id,
				name: data.name,
				unreadCount: 0,
			});
		} catch (err) {
			toastError(err);
		}
	};

	const handleStartDm = () => {
		if (!dmUserId) return;
		const partner = users.find((u) => u.id === +dmUserId);
		if (!partner) return;
		setDmDialogOpen(false);
		setDmUserId("");
		handleSelectConversation({
			type: "direct",
			userId: partner.id,
			name: partner.name,
			unreadCount: 0,
		});
	};

	const abrirIA = () => {
		setAiVersoes([]);
		setAiSugestoes([]);
		setAiErro("");
		const digitado = String(inputMessage || "").trim();
		setAiModo(digitado ? "corrigir" : "sugerir");
		setAiAberto(true);
		if (digitado) aiGerarVersao("corrigir", "Corrigido");
		else aiBuscarSugestoes();
	};

	const aiGerarVersao = async (acao, rotulo) => {
		if (aiCarregando) return;
		const digitado = String(inputMessage || "").trim();
		if (!digitado) {
			setAiErro("Digite algo primeiro para corrigir.");
			return;
		}
		setAiCarregando(acao);
		setAiErro("");
		try {
			const { data } = await api.post("/ai/improve", { text: digitado, action: acao });
			const resultado = String(data?.texto || "").trim();
			if (!resultado) { setAiErro("A IA não devolveu texto."); return; }
			setAiVersoes((a) => [...a.filter((v) => v.acao !== acao), { acao, rotulo, texto: resultado }]);
		} catch (err) {
			setAiErro("A IA não devolveu uma correção utilizável. Tente novamente.");
		} finally {
			setAiCarregando("");
		}
	};

	const aiBuscarSugestoes = async () => {
		if (aiCarregando) return;
		if (!selected) { setAiErro("Selecione uma conversa primeiro."); return; }
		setAiCarregando("sugerir");
		setAiErro("");
		try {
			const ultimas = messages.slice(-20);
			const nomeDest = selected.name || "a pessoa";
			const eu = user?.name || "você";
			const linhas = ultimas.map((m) => {
				const autor = +m.fromUserId === +user?.id ? eu : m.fromUser?.name || nomeDest;
				const corpo = m.body || (m.mediaUrl ? "[anexo]" : "");
				return `${autor}: ${String(corpo).replace(/\s+/g, " ").slice(0, 200)}`;
			}).join("\n");
			const digitado = String(inputMessage || "").trim();
			const prompt = digitado
				? `Continue esta conversa interna de equipe a partir daqui.\n\nContexto:\n${linhas}\n\n${eu} está digitando: "${digitado}"\n\nEscreva 3 opções curtas e naturais de resposta para ${eu} enviar (pt-BR, informal). Devolva um JSON com o campo "sugestoes" (array de 3 strings).`
				: `Responda esta conversa interna de equipe.\n\nContexto:\n${linhas}\n\nNome de quem vai responder: ${eu}. Destinatário: ${nomeDest}.\n\nEscreva 3 opções curtas e naturais para ${eu} responder (pt-BR, tom de equipe). Devolva um JSON com o campo "sugestoes" (array de 3 strings).`;
			const { data } = await api.post("/ai/improve", { text: prompt, action: "melhorar", json: true });
			let lista = [];
			if (data?.sugestoes && Array.isArray(data.sugestoes)) lista = data.sugestoes;
			else if (typeof data?.texto === "string") {
				try {
					const p = JSON.parse(data.texto);
					if (p?.sugestoes && Array.isArray(p.sugestoes)) lista = p.sugestoes;
				} catch (_) {
					const linhas = String(data.texto).split("\n").map((l) => l.replace(/^\s*[-*0-9.^) ]+\s*/, "").trim()).filter(Boolean).slice(0, 3);
					lista = linhas;
				}
			}
			lista = lista.filter(Boolean);
			setAiSugestoes(lista);
			if (!lista.length) setAiErro("A IA não sugeriu nada desta vez.");
		} catch (err) {
			setAiErro("Não consegui falar com a IA.");
			toastError(err);
		} finally {
			setAiCarregando("");
		}
	};

	const aiUsar = (novoTexto) => {
		setInputMessage(novoTexto);
		setAiAberto(false);
		toast.success("Texto colocado na caixa de mensagem.");
		setTimeout(() => document.querySelector("[data-internal-chat-input]")?.focus?.(), 50);
	};

	const availableUsers = users.filter((u) => u.id !== user?.id);

	return (
		<MainContainer>
			<div className="flex flex-1 overflow-hidden">
				{/* Left column: conversations */}
				<div
					className="flex w-80 shrink-0 flex-col border-r"
					style={{ backgroundColor: tema.bgSidebar, borderRightColor: tema.borderSeparator }}
				>
					<div
						className="flex items-center justify-between gap-2 border-b px-4 py-3"
						style={{ backgroundColor: tema.bgHeader, borderBottomColor: tema.borderSeparator }}
					>
						<h2 className="text-base font-semibold" style={{ color: tema.corTextoRecebida }}>Chat interno</h2>
						<div className="flex gap-1">
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								title="Nova conversa"
								onClick={() => setDmDialogOpen(true)}
							>
								<MessageSquarePlus className="h-4 w-4" />
							</Button>
							<Button
								size="icon"
								className="h-8 w-8"
								title="Novo grupo"
								onClick={() => setGroupDialogOpen(true)}
							>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
					</div>
					<div className="flex-1 overflow-y-auto">
						{conversations.length === 0 && (
							<p className="px-4 py-6 text-center text-sm text-muted-foreground">
								Nenhuma conversa ainda. Comece uma nova conversa ou crie um
								grupo.
							</p>
						)}
						{conversations.map((conv) => {
							const isSelected =
								selected && conversationKey(conv) === conversationKey(selected);
							return (
								<button
									key={conversationKey(conv)}
									type="button"
									className={`flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors`}
									style={{
										borderBottomColor: tema.borderSeparator,
										backgroundColor: isSelected ? tema.bgSidebarItemSelected : "transparent",
										color: tema.corTextoRecebida,
									}}
									onMouseEnter={(e) => {
										if (!isSelected) e.currentTarget.style.backgroundColor = tema.bgSidebarHover;
									}}
									onMouseLeave={(e) => {
										if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
									}}
									onClick={() => handleSelectConversation(conv)}
								>
									{conv.type === "group" ? (
										<div
											className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
											style={{ backgroundColor: tema.avatarBgGrupo, color: tema.avatarIconColor }}
										>
											<Users className="h-5 w-5" />
										</div>
									) : (
										<AvatarUsuario
											name={conv.name}
											profilePicUrl={conv.profilePicUrl ?? conv.user?.profilePicUrl}
											size="md"
										/>
									)}
									<div className="min-w-0 flex-1">
										<div className="flex items-center justify-between gap-2">
											<p className="truncate text-sm font-medium">{conv.name}</p>
											{conv.lastMessage && (
												<span
													className="shrink-0 text-[11px]"
													style={{ color: tema.textoMuted }}
												>
													{formatTime(conv.lastMessage.createdAt)}
												</span>
											)}
										</div>
										<div className="flex items-center justify-between gap-2">
											<p
												className="truncate text-xs"
												style={{ color: tema.textoMuted }}
											>
												{conv.lastMessage?.body || "Sem mensagens"}
											</p>
											{conv.unreadCount > 0 && (
												<Badge
													className="h-5 min-w-5 shrink-0 justify-center rounded-full px-1.5 text-[11px] font-semibold text-white border-0"
													style={{ backgroundColor: tema.badgeUnread }}
												>
													{conv.unreadCount}
												</Badge>
											)}
										</div>
									</div>
								</button>
							);
						})}
					</div>
				</div>

				{/* Right pane: messages */}
				<div
					className="flex flex-1 flex-col overflow-hidden"
					style={{ backgroundColor: tema.bgEmptyChat }}
				>
					{!selected ? (
						<div
							className="flex flex-1 items-center justify-center"
							style={{ backgroundColor: tema.bgEmptyChat, color: tema.textoMuted }}
						>
							<div className="text-center">
								<div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: tema.avatarBgGrupo, color: tema.avatarIconColor }}>
									<MessageCircle className="h-8 w-8" />
								</div>
								<p className="font-medium" style={{ color: tema.corTextoRecebida }}>Chat interno da equipe</p>
								<p className="mt-1 text-xs max-w-sm">Selecione uma conversa para começar</p>
							</div>
						</div>
					) : (
						<>
							<div
								className="flex items-center gap-3 border-b px-4 py-2.5"
								style={{ backgroundColor: tema.bgHeader, borderColor: tema.borderSeparator }}
							>
								{selected.type === "group" ? (
									<div
										className="flex h-9 w-9 items-center justify-center rounded-full"
										style={{ backgroundColor: tema.avatarBgGrupo, color: tema.avatarIconColor }}
									>
										<Users className="h-4 w-4" />
									</div>
								) : (
									<AvatarUsuario
										name={selected.name}
										profilePicUrl={selected.profilePicUrl ?? selected.user?.profilePicUrl}
										size="lg"
									/>
								)}
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-semibold">{selected.name}</p>
									<p
										className="truncate text-xs"
										style={{ color: tema.textoMuted }}
									>
										{selected.type === "group" ? "Grupo" : "Conversa direta"}
									</p>
								</div>
								{/* =====================================================
								    BUSCA de mensagens (igual widget, igual WhatsApp):
								    botão lupa no header, abre input abaixo.
								   ===================================================== */}
								<Button
									variant="ghost"
									size="icon"
									className={`h-9 w-9 ${buscaAberta ? "bg-[#00a884]/10" : ""}`}
									title="Buscar na conversa"
									aria-label="Buscar na conversa"
									onClick={toggleBusca}
								>
									<Search
										className="h-5 w-5"
										style={{ color: buscaAberta ? "#008069" : tema.textoMuted }}
									/>
								</Button>
								{selected.type === "group" && (
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												className="h-9 w-9"
												title="Opções do grupo"
											>
												<MoreVertical className="h-5 w-5" />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" className="w-48">
											<DropdownMenuItem
												className="text-destructive focus:text-destructive"
												onSelect={() => setDeleteGroupDialogOpen(true)}
											>
												<Trash2 className="mr-2 h-4 w-4" />
												Excluir grupo
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							</div>

							{/* =====================================================
							    BARRA DE BUSCA ABAIXO DO HEADER
							    (igual widget e igual WhatsApp)
							   ===================================================== */}
							{buscaAberta && (
								<div
									className="flex items-center gap-2 border-b px-3 py-2"
									style={{
										backgroundColor: tema.bgHeader,
										borderColor: tema.borderSeparator,
									}}
								>
									<Search
										className="h-4 w-4 shrink-0"
										style={{ color: tema.textoMuted }}
									/>
									<Input
										data-busca-msg-interna-pagina
										placeholder="Buscar mensagens na conversa..."
										value={buscaMsg}
										onChange={(e) => {
											setBuscaMsg(e.target.value);
											setIndiceMatch(0);
										}}
										autoFocus
										className="h-8 min-h-8 border shadow-none focus-visible:ring-1"
										style={{
											backgroundColor: tema.inputBg,
											color: tema.corTextoRecebida,
										}}
									/>
									<div className="flex items-center gap-1">
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											title="Anterior"
											disabled={idsMatchesBusca.length === 0}
											onClick={() =>
												setIndiceMatch((i) =>
													idsMatchesBusca.length === 0
														? 0
														: i <= 0
														? idsMatchesBusca.length - 1
														: i - 1
												)
											}
										>
											<ChevronUp
												className="h-4 w-4"
												style={{ color: tema.textoMuted }}
											/>
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											title="Próximo"
											disabled={idsMatchesBusca.length === 0}
											onClick={() =>
												setIndiceMatch((i) =>
													idsMatchesBusca.length === 0
														? 0
														: i >= idsMatchesBusca.length - 1
														? 0
														: i + 1
												)
											}
										>
											<ChevronDown
												className="h-4 w-4"
												style={{ color: tema.textoMuted }}
											/>
										</Button>
										<span
											className="min-w-[48px] shrink-0 text-right text-[11px] tabular-nums"
											style={{ color: tema.textoMuted }}
										>
											{idsMatchesBusca.length === 0
												? "Sem resultados"
												: `${Math.min(indiceMatch + 1, idsMatchesBusca.length)} / ${idsMatchesBusca.length}`}
										</span>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											title="Fechar busca"
											onClick={toggleBusca}
										>
											<X
												className="h-4 w-4"
												style={{ color: tema.textoMuted }}
											/>
										</Button>
									</div>
								</div>
							)}

							<div
								ref={scrollListRef}
								className="flex-1 space-y-2 overflow-y-auto px-4 py-4"
								style={{
									backgroundColor: tema.bgChat,
									backgroundImage: tema.bgChatDots,
									backgroundRepeat: "repeat",
									backgroundSize: "400px 400px",
								}}
							>
								{loadingMessages && (
									<div className="flex justify-center py-4" style={{ color: tema.textoMuted }}>
										<Loader2 className="h-5 w-5 animate-spin" />
									</div>
								)}
								{!loadingMessages && messages.length === 0 && (
									<p className="py-6 text-center text-sm" style={{ color: tema.textoMuted }}>
										Nenhuma mensagem ainda. Envie a primeira!
									</p>
								)}
								{messages.map((message) => {
									const isMine = message.fromUserId === user?.id;
									const temReacoes = Array.isArray(message.reacoes) && message.reacoes.length > 0;
									const showActions = reactionPopoverFor === message.id;
									const isEditing = editingMessageId === message.id;
									const podeEditar = podeEditarMensagem(message);
									const ehMatchAtual = idsMatchesBusca[indiceMatch] === message.id;
									return (
										<div
											key={message.id}
											className={`relative flex items-end gap-2 ${isMine ? "justify-end" : "justify-start"}`}
											data-reaction-wrapper
											data-msg-id={message.id}
										>
											{!isMine && selected.type === "group" ? (
												<div data-skip-react className="shrink-0">
													<AvatarUsuario user={message.fromUser} size="sm" />
												</div>
											) : !isMine ? (
												<div className="h-6 w-6 shrink-0" aria-hidden />
											) : null}
											<div
												className={`relative max-w-[80%] rounded-[7.5px] px-2.5 py-1.5 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] space-y-1 ${isEditing ? "select-text" : "select-none"} ${ehMatchAtual ? "ring-2 ring-[#ffcc4d]" : ""}`}
												ref={(el) => {
													if (el) {
														balaoRefsSet(message.id, { el, isMine });
													} else {
														balaoRefsSet(message.id, null);
													}
												}}
												style={{
													backgroundColor: isMine ? tema.balaoEnviada : tema.balaoRecebida,
													color: isMine ? tema.corTextoEnviada : tema.corTextoRecebida,
													borderTopRightRadius: isMine ? "2px" : undefined,
													borderTopLeftRadius: !isMine ? "2px" : undefined,
												}}
											>
												{!isMine && selected.type === "group" && (
													<p
														className="mb-0.5 text-[13px] font-semibold leading-tight"
														style={{ color: tema.verdeWppEscuro }}
													>
														{message.fromUser?.name || "Usuário"}
													</p>
												)}
												{message.quotedMsg && (
													<div
														className={`flex items-start gap-2 rounded-md border-l-[4px] pl-2 pr-2 py-1.5`}
														style={{
															backgroundColor: isMine
																? tema.bgQuotedEnviada
																: tema.bgQuotedRecebida,
															borderLeftColor: isMine
																? tema.borderQuotedMinha
																: tema.borderQuoted,
														}}
													>
														<div className="min-w-0 flex-1">
															<p
																className={`truncate text-[12px] font-semibold`}
																style={{
																	color: isMine
																		? tema.borderQuotedMinha
																		: tema.borderQuoted,
																}}
															>
																{nomeUsuarioNaMsg(message.quotedMsg)}
															</p>
															<p
																className="mt-0.5 line-clamp-2 text-[12px]"
																style={{ color: tema.corTextoRecebida }}
															>
																{previewMensagemCitada(message.quotedMsg)}
															</p>
														</div>
														{message.quotedMsg.mediaUrl &&
															(message.quotedMsg.mediaType || tipoDeMidia(message.quotedMsg.mediaUrl)) ===
																"image" && (
																<div className="h-10 w-10 shrink-0 overflow-hidden rounded border">
																	<img
																		src={getPublicFileUrl(message.quotedMsg.mediaUrl)}
																		alt=""
																		className="h-full w-full object-cover"
																	/>
																</div>
															)}
													</div>
												)}
												{message.mediaUrl && (
													<RenderizarMidia
														message={message}
														onAbrirImagem={(url, nome) => setImagemModal({ url, nome })}
														abrirArquivo={abrirArquivo}
														transcrevendo={transcrevendoIds.includes(+message.id)}
														transcription={message.transcription}
														transcribedAt={message.transcribedAt}
														onTranscrever={(opts) => handleTranscrever(message, opts || {})}
													/>
												)}
												{isEditing ? (
													<div className="space-y-2" data-skip-react>
														<textarea
															autoFocus
															value={editingBody}
															onChange={(e) => setEditingBody(e.target.value)}
															onKeyDown={(e) => {
																if (e.key === "Enter" && !e.shiftKey) {
																	e.preventDefault();
																	saveEditMessage();
																}
																if (e.key === "Escape") {
																	e.preventDefault();
																	cancelEditMessage();
																}
															}}
															className="w-full resize-y rounded-md border px-2 py-1 text-[14.2px] leading-[19px] outline-none focus:ring-2"
															style={{
																minHeight: "56px",
																backgroundColor: isMine ? tema.balaoEnviada : tema.balaoRecebida,
																color: isMine ? tema.corTextoEnviada : tema.corTextoRecebida,
																borderColor: tema.popoverBorder,
															}}
														/>
														<div className={`flex items-center gap-2 ${isMine ? "justify-end" : "justify-start"}`}>
															<Button
																type="button"
																size="sm"
																variant="ghost"
																onClick={(e) => {
																	e.stopPropagation();
																	cancelEditMessage();
																}}
																className="h-7 px-2 text-xs"
															>
																<X className="h-3.5 w-3.5 mr-1" /> Cancelar
															</Button>
															<Button
																type="button"
																size="sm"
																onClick={(e) => {
																	e.stopPropagation();
																	saveEditMessage();
																}}
																className="h-7 px-2 text-xs text-white"
																style={{ backgroundColor: tema.verdeWpp }}
																disabled={savingEdit}
															>
																<Check className="h-3.5 w-3.5 mr-1" />
																{savingEdit ? "Salvando..." : "Salvar"}
															</Button>
														</div>
													</div>
												) : message.body ? (
													<p
														className="whitespace-pre-wrap break-words text-[14.2px] leading-[19px] select-text"
														style={{
															wordBreak: "break-word",
															overflowWrap: "anywhere",
															hyphens: "auto",
														}}
													>
														{destacarTexto(message.body, buscaMsg, ehMatchAtual)}
													</p>
												) : null}
												<div className="flex items-end justify-between gap-2 mt-1">
													{temReacoes ? (
														<div className={`flex flex-wrap gap-1 justify-start min-w-0 ${!isMine ? "" : ""}`}>
															{message.reacoes.map((r) => {
																const euReagi = Array.isArray(r.userIds) && r.userIds.includes(user?.id);
																const total = Number(r.total) || r.userIds?.length || 1;
																return (
																	<button
																		type="button"
																		key={String(r.emoji)}
																		data-skip-react
																		onClick={(e) => {
																			e.stopPropagation();
																			toggleReaction(message.id, r.emoji);
																		}}
																		className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors`}
																		style={{
																			backgroundColor: euReagi
																				? isMine
																					? tema.bgChipReactionEuMinha
																					: tema.bgChipReactionEu
																				: isMine
																					? tema.bgChipReactionMinha
																					: tema.bgChipReaction,
																			borderColor: isMine ? tema.balaoEnviada : tema.balaoRecebida,
																			boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
																			color: isMine ? tema.corTextoEnviada : tema.corTextoRecebida,
																		}}
																		title={`${r.userIds?.length || 0} reagiram com ${r.emoji}`}
																	>
																		<span className="text-sm leading-none">
																			{r.emoji}
																		</span>
																		<span className="font-semibold" style={{ color: tema.textoMuted }}>
																			{total}
																		</span>
																	</button>
																);
															})}
														</div>
													) : (
														<span className="flex-1" aria-hidden />
													)}
													<p
														className={`pointer-events-none shrink-0 flex items-center justify-end gap-1 text-[10px] leading-none ml-2 ${
															temReacoes ? "" : ""
														}`}
														style={{ color: tema.textoMuted }}
													>
														{message.editedAt && (
															<span className="italic opacity-70 mr-1">editado</span>
														)}
														<span className="whitespace-nowrap">{formatTime(message.createdAt)}</span>
													</p>
												</div>
											</div>
										</div>
									);
								})}
								<div ref={messagesEndRef} />
							</div>

							<div
								className="flex flex-col border-t"
								style={{ backgroundColor: tema.bgInput, borderTopColor: tema.borderSeparator }}
							>
								{quotedMessage && (
									<div
										className="flex items-center gap-2 border-b px-4 py-2"
										style={{
											borderBottomColor: tema.borderSeparator,
											backgroundColor: tema.barraResponderBg,
										}}
									>
										<div
											className="h-8 w-[3px] shrink-0 self-stretch rounded-full"
											style={{ backgroundColor: tema.borderQuoted }}
										/>
										<div className="min-w-0 flex-1">
											<p
												className="truncate text-[11px] font-semibold"
												style={{ color: tema.borderQuoted }}
											>
												Respondendo a {nomeUsuarioNaMsg(quotedMessage)}
											</p>
											<p
												className="mt-0.5 truncate text-xs"
												style={{ color: tema.textoMuted }}
											>
												{previewMensagemCitada(quotedMessage)}
											</p>
										</div>
										{quotedMessage.mediaUrl &&
											(quotedMessage.mediaType || tipoDeMidia(quotedMessage.mediaUrl)) ===
												"image" && (
												<div className="h-10 w-10 shrink-0 overflow-hidden rounded border">
													<img
														src={getPublicFileUrl(quotedMessage.mediaUrl)}
														alt=""
														className="h-full w-full object-cover"
													/>
												</div>
											)}
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 shrink-0"
											onClick={cancelReply}
											title="Cancelar resposta"
										>
											<X className="h-4 w-4" style={{ color: tema.textoMuted }} />
										</Button>
									</div>
								)}
								{medias.length > 0 && (
									<div
										className="flex flex-wrap gap-2 border-b px-4 py-2"
										style={{ borderBottomColor: tema.borderSeparator }}
									>
										{medias.map((arq, idx) => {
											const tipo = tipoDeMidia(arq.name);
											return (
												<div
													key={`${arq.name}-${arq.size}-${arq.lastModified}-${idx}`}
													className="flex items-center gap-2 rounded-xl border px-3 py-1.5"
													style={{
														backgroundColor: tema.chipAnexoBg,
														borderColor: tema.borderSeparator,
													}}
												>
													<div style={{ color: tema.verdeWppEscuro }}>
														{iconeDoAnexo(tipo, "h-4 w-4")}
													</div>
													<div className="min-w-0">
														<p className="truncate text-xs font-medium max-w-[140px]">
															{arq.name}
														</p>
														<p
															className="truncate text-[10px]"
															style={{ color: tema.textoMuted }}
														>
															{humanFileSize(arq.size)}
														</p>
													</div>
													<button
														type="button"
														onClick={() => handleRemoveMedia(idx)}
														className="ml-1 rounded-full p-0.5 transition-colors hover:bg-gray-100"
														style={{ color: tema.textoMuted }}
														title="Remover"
													>
														<X className="h-4 w-4" />
													</button>
												</div>
											);
										})}
									</div>
								)}
								<div className="flex items-center gap-2 px-4 py-2.5">
									<input
										ref={fileInputRef}
										type="file"
										multiple
										accept={ACCEPT_STRING}
										className="hidden"
										onChange={handleFilesChanged}
									/>
									<Button
										variant="ghost"
										size="icon"
										className="h-10 w-10 shrink-0"
										title="Anexar mídia (imagem, áudio, vídeo ou documento)"
										onClick={handlePickFiles}
										disabled={recording}
									>
										<Paperclip className="h-5 w-5" style={{ color: tema.textoMuted }} />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-10 w-10 shrink-0"
										title="Figurinhas"
										aria-label="Figurinhas"
										ref={figurinhaAncoraRef}
										onClick={() => setFigurinhaAberto((a) => !a)}
										disabled={recording || !selected}
									>
										<Sticker
											className="h-5 w-5"
											style={{ color: figurinhaAberto ? "#008069" : tema.textoMuted }}
										/>
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-10 w-10 shrink-0"
										title="Corrigir & sugestão (IA)"
										onClick={abrirIA}
										disabled={recording || !selected}
									>
										<Sparkles className="h-5 w-5" style={{ color: tema.textoMuted }} />
									</Button>
									{!recording && !inputMessage.trim() && medias.length === 0 ? (
										<Button
											variant="ghost"
											size="icon"
											className="h-10 w-10 shrink-0"
											title="Gravar mensagem de áudio (clique para começar)"
											onClick={handleStartRecord}
										>
											<Mic className="h-5 w-5" style={{ color: tema.textoMuted }} />
										</Button>
									) : null}
									{recording ? (
										<div
											className="flex h-10 flex-1 items-center justify-between rounded-lg border-0 px-3 shadow-none"
											style={{ backgroundColor: tema.inputBg, borderRadius: "8px" }}
										>
											<div className="flex items-center gap-2">
												<RecordingTimer />
											</div>
											<div className="flex items-center gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 shrink-0"
													title="Cancelar gravação"
													onClick={handleCancelAudio}
												>
													<X className="h-5 w-5 text-red-500" />
												</Button>
												<Button
													size="icon"
													className="h-8 w-8 shrink-0 border-0"
													style={{ backgroundColor: tema.verdeWpp, color: "#ffffff" }}
													title="Enviar áudio"
													onClick={handleUploadAudio}
												>
													<Check className="h-5 w-5" />
												</Button>
											</div>
										</div>
									) : (
										<Input
											data-internal-chat-input
											placeholder="Digite uma mensagem... (cole imagens/documentos aqui)"
											value={inputMessage}
											onChange={(e) => setInputMessage(e.target.value)}
											onPaste={handlePaste}
											onKeyDown={(e) => {
												if (e.key === "Enter" && !e.shiftKey) {
													e.preventDefault();
													handleSendMessage();
												}
											}}
											className="h-10 min-h-10 max-h-32 border-0 shadow-none focus-visible:ring-0"
											style={{
												backgroundColor: tema.inputBg,
												color: tema.corTextoRecebida,
												borderRadius: "8px",
												paddingLeft: "14px",
												boxShadow: "none",
											}}
										/>
									)}
									{!recording ? (
										<>
											<AssistenteDeTexto
												ticketId={null}
												texto={inputMessage}
												aoTrocarTexto={setInputMessage}
												desabilitado={
													(!inputMessage.trim() && medias.length === 0) ||
													savingEdit ||
													recording ||
													!selected
												}
											/>
											<Button
												size="icon"
												className="h-10 w-10 shrink-0 border-0"
												style={{
													backgroundColor: tema.verdeWpp,
													color: "#ffffff",
												}}
												onClick={handleSendMessage}
												disabled={!inputMessage.trim() && medias.length === 0}
											>
												<Send className="h-5 w-5" />
											</Button>
										</>
									) : null}
								</div>
							</div>
							<FigurinhaPicker
								aberto={figurinhaAberto && !!selected}
								aoFechar={() => setFigurinhaAberto(false)}
								ticketId={null}
								ancoraRef={figurinhaAncoraRef}
								quotedMsg={quotedMessage}
								customOnSelectSticker={(fig) => enviarFigurinhaPagina(fig)}
							/>
						</>
					)}
				</div>
			</div>

			{/* Copiloto IA */}
			<Dialog open={aiAberto} onOpenChange={(o) => { if (aiCarregando === "") setAiAberto(o); }}>
				<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-primary" />
							Corrigir &amp; sugestão (Chat Interno)
						</DialogTitle>
					</DialogHeader>

					<div className="flex gap-1 rounded-lg bg-muted p-1">
						<button
							type="button"
							onClick={() => {
								setAiModo("corrigir");
								setAiErro("");
								if (!aiVersoes.length && String(inputMessage || "").trim()) aiGerarVersao("corrigir", "Corrigido");
							}}
							disabled={!String(inputMessage || "").trim()}
							className={
								"flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors "
								+ (aiModo === "corrigir"
									? "bg-background shadow-sm"
									: "text-muted-foreground hover:text-foreground "
									+ (!String(inputMessage || "").trim() ? "cursor-not-allowed opacity-50" : ""))
							}
						>
							<SpellCheck className="h-3.5 w-3.5" />
							Corrigir meu texto
						</button>
						<button
							type="button"
							onClick={() => {
								setAiModo("sugerir");
								setAiErro("");
								if (!aiSugestoes.length) aiBuscarSugestoes();
							}}
							className={
								"flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors "
								+ (aiModo === "sugerir" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")
							}
						>
							<MessageSquare className="h-3.5 w-3.5" />
							Sugerir resposta
						</button>
					</div>

					<p className="text-xs text-muted-foreground">
						{aiModo === "corrigir"
							? "Toque na versão que quiser usar. Ela vai para o campo de mensagem."
							: "A IA lê a conversa e propõe respostas. Nada é enviado sem você confirmar."}
					</p>

					{aiErro && <p className="text-sm text-destructive">{aiErro}</p>}

					{aiModo === "corrigir" ? (
						<div className="space-y-2">
							{aiVersoes.map((v) => (
								<button
									key={v.acao}
									type="button"
									onClick={() => aiUsar(v.texto)}
									className="group w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
								>
									<div className="flex items-center justify-between gap-2 pb-1">
										<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
											{v.rotulo}
										</span>
										<span className="flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
											<Check className="h-3 w-3" /> usar
										</span>
									</div>
									<p className="whitespace-pre-wrap text-sm leading-relaxed">{v.texto}</p>
								</button>
							))}

							{aiCarregando && aiCarregando !== "sugerir" && (
								<div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" />
									Revisando o texto…
								</div>
							)}

							<div className="flex flex-wrap gap-1.5 pt-0.5">
								{TONS_IA.filter(
									(t) => !aiVersoes.some((v) => v.acao === t.acao)
								).map((t) => (
									<Button
										key={t.acao}
										type="button"
										variant="outline"
										size="sm"
										className="h-7 text-xs"
										onClick={() => aiGerarVersao(t.acao, t.rotulo)}
										disabled={Boolean(aiCarregando) || !String(inputMessage || "").trim()}
									>
										{aiCarregando === t.acao && (
											<Loader2 className="h-3 w-3 animate-spin" />
										)}
										{t.rotulo}
									</Button>
								))}
							</div>
						</div>
					) : (
						<div className="space-y-2">
							{aiCarregando === "sugerir" ? (
								<div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" />
									Lendo a conversa…
								</div>
							) : (
								aiSugestoes.map((sugestao, i) => (
									<button
										key={i}
										type="button"
										onClick={() => aiUsar(sugestao)}
										className="group w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
									>
										<div className="flex items-center justify-between gap-2 pb-1">
											<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
												Sugestão {i + 1}
											</span>
											<span className="flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
												<Check className="h-3 w-3" /> usar
											</span>
										</div>
										<p className="whitespace-pre-wrap text-sm leading-relaxed">{sugestao}</p>
									</button>
								))
							)}

							{!aiCarregando && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-7 w-full text-xs"
									onClick={aiBuscarSugestoes}
									disabled={!selected || Boolean(aiCarregando)}
								>
									{aiSugestoes.length ? "Gerar outras" : "Tentar de novo"}
								</Button>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* New group dialog */}
			<Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Novo grupo</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="group-name">Nome do grupo</Label>
							<Input
								id="group-name"
								value={groupName}
								autoFocus
								onChange={(e) => setGroupName(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>Membros</Label>
							<MultiSelect
								options={availableUsers.map((u) => ({
									value: u.id,
									label: u.name,
								}))}
								value={groupMemberIds}
								onChange={setGroupMemberIds}
								placeholder="Selecione os membros"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setGroupDialogOpen(false)}>
							Cancelar
						</Button>
						<Button onClick={handleCreateGroup} disabled={!groupName.trim()}>
							Criar grupo
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete group dialog */}
			<Dialog open={deleteGroupDialogOpen} onOpenChange={(open) => {
				if (!deletingGroup) setDeleteGroupDialogOpen(open);
			}}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-destructive">
							<AlertTriangle className="h-5 w-5" />
							Excluir grupo
						</DialogTitle>
						<DialogDescription>
							Tem certeza que deseja excluir o grupo{" "}
							<strong className="font-semibold text-foreground">
								&quot;{selected?.name || "este grupo"}&quot;
							</strong>
							? Esta ação não pode ser desfeita — todas as mensagens, anexos e
							reações serão apagadas permanentemente.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setDeleteGroupDialogOpen(false)}
							disabled={deletingGroup}
						>
							Cancelar
						</Button>
						<Button
							variant="destructive"
							onClick={confirmDeleteGroup}
							disabled={deletingGroup}
						>
							{deletingGroup && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							Excluir permanentemente
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* New DM dialog */}
			<Dialog open={dmDialogOpen} onOpenChange={setDmDialogOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Nova conversa</DialogTitle>
					</DialogHeader>
					<div className="space-y-1.5">
						<Label>Usuário</Label>
						<Select value={dmUserId} onValueChange={setDmUserId}>
							<SelectTrigger>
								<SelectValue placeholder="Selecione um usuário" />
							</SelectTrigger>
							<SelectContent>
								{availableUsers.map((u) => (
									<SelectItem key={u.id} value={String(u.id)}>
										{u.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDmDialogOpen(false)}>
							Cancelar
						</Button>
						<Button onClick={handleStartDm} disabled={!dmUserId}>
							Iniciar conversa
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			{popupRect && popupAbertoMsg && typeof document !== "undefined" && (() => {
				const POPUP_W = 336;
				const POPUP_H = 56;
				const MARGIN = 12;
				const viewW = typeof window !== "undefined" ? window.innerWidth : 1200;
				const viewH = typeof window !== "undefined" ? window.innerHeight : 900;
				let leftCalc = popupRect.isMine
					? Math.max(MARGIN, popupRect.left + popupRect.width - POPUP_W)
					: Math.max(MARGIN, popupRect.left);
				leftCalc = Math.max(MARGIN, Math.min(leftCalc, viewW - POPUP_W - MARGIN));
				const topAcima = popupRect.top - POPUP_H - 6;
				let topCalc = topAcima >= MARGIN
					? Math.max(MARGIN, topAcima)
					: Math.min(viewH - POPUP_H - MARGIN, popupRect.bottom + 8);
				const dbgPos = JSON.stringify({
					winW: viewW,
					winH: viewH,
					rectL: Math.round(popupRect.left),
					rectR: Math.round(popupRect.left + popupRect.width),
					isMine: popupRect.isMine,
					leftPx: Math.round(leftCalc),
					topPx: Math.round(topCalc),
					estouraDir: Math.round(leftCalc + POPUP_W) > viewW - MARGIN ? 1 : 0,
				});
				return createPortal(
				<div
					ref={popupHostRef}
					data-popup-host-internal
					data-reaction-popover
					data-skip-react
					className="pointer-events-auto fixed z-[999999]"
					style={{
						top: topCalc,
						left: leftCalc,
						maxWidth: "min(336px, 94vw)",
					}}
					data-dbg-pos={dbgPos}
				>
					<EmojiPopoverInline
						onPick={(e) => {
							dbgPush({ ev: "PORTAL_onPick_call", emoji: e, msgId: String(popupAbertoMsg.id) });
							reactionPopoverForRef.current = null;
							return toggleReaction(popupAbertoMsg.id, e);
						}}
						onReply={() => {
							dbgPush({ ev: "PORTAL_onReply_call", msgId: String(popupAbertoMsg.id) });
							reactionPopoverForRef.current = null;
							return replyToMessage(popupAbertoMsg);
						}}
						onEdit={() => {
							dbgPush({ ev: "PORTAL_onEdit_call", msgId: String(popupAbertoMsg.id) });
							reactionPopoverForRef.current = null;
							return startEditMessage(popupAbertoMsg);
						}}
						onClose={() => {
							dbgPush({ ev: "PORTAL_onClose_call" });
							reactionPopoverForRef.current = null;
							setReactionPopoverFor(null);
						}}
						canEdit={podeEditarMensagem(popupAbertoMsg)}
					/>
				</div>,
				document.body
			)})()}
			{imagemModal &&
				typeof document !== "undefined" &&
				createPortal(
					<div
						data-skip-react
						className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
						onClick={(e) => {
							if (e.target === e.currentTarget) setImagemModal(null);
						}}
						role="dialog"
						aria-modal="true"
						aria-label={imagemModal.nome || "Imagem"}
					>
						<button
							type="button"
							aria-label="Fechar pré-visualização"
							className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20"
							onClick={(e) => {
								e.stopPropagation();
								setImagemModal(null);
							}}
						>
							<X className="h-5 w-5" />
						</button>
						<div className="relative max-h-full max-w-full overflow-hidden rounded-md">
							<img
								src={imagemModal.url}
								alt={imagemModal.nome || "Imagem enviada"}
								className="max-h-[90vh] max-w-[92vw] rounded-md object-contain"
							/>
						</div>
						<button
							type="button"
							aria-label="Abrir original em nova aba"
							className="absolute bottom-4 right-4 inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-4 text-sm text-white backdrop-blur hover:bg-white/20"
							onClick={(e) => {
								e.stopPropagation();
								window.open(imagemModal.url, "_blank", "noopener,noreferrer");
							}}
						>
							Abrir original
						</button>
					</div>,
					document.body
				)}
			<VisualizadorDeArquivo
				open={visualizando !== null}
				onClose={() => setVisualizando(null)}
				arquivos={arquivosDaConversa}
				indice={visualizando ?? 0}
				onIndice={setVisualizando}
			/>
		</MainContainer>
	);
};

export default InternalChat;
